import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import DocumentList from '../components/DocumentList';
import { marked } from 'marked';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const GENERAL_WELLNESS_TIPS = `🏃 Stay active with 30 minutes of exercise daily
💧 Drink 8 glasses of water throughout the day
🥗 Eat a balanced diet with plenty of vegetables
😴 Get 7-8 hours of quality sleep each night
🚫 Limit sugar, salt and processed foods
🩺 Schedule regular health checkups with your doctor`;

// Timestamped log of past AI generations; clicking an entry restores it
const GenerationHistory = ({ history, show, setShow, onSelect, currentShownAt }) => {
  if (!history || history.length === 0) return null;
  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };
  return (
    <div style={{ marginTop: '8px' }}>
      <button
        onClick={() => setShow(!show)}
        style={{ border: 'none', background: 'none', color: '#2b6cb0',
          cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
      >
        {show ? '▾' : '▸'} History ({history.length})
      </button>
      {show && (
        <div style={{ marginTop: '6px', border: '1px solid #e2e8f0', borderRadius: '8px',
          maxHeight: '180px', overflowY: 'auto' }}>
          {history.map((rec, i) => {
            const isShown = currentShownAt &&
              new Date(rec.created_at).getTime() === currentShownAt.getTime();
            return (
              <button
                key={rec.id || i}
                onClick={() => onSelect(rec)}
                style={{ display: 'flex', width: '100%', justifyContent: 'space-between',
                  alignItems: 'center', gap: '8px', padding: '7px 12px', border: 'none',
                  borderBottom: i < history.length - 1 ? '1px solid #edf2f7' : 'none',
                  background: isShown ? '#ebf8ff' : 'white', cursor: 'pointer',
                  textAlign: 'left' }}
              >
                <span style={{ fontSize: '0.82rem', color: '#4a5568', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                  {(rec.content || '').replace(/[#*`>-]/g, '').trim().slice(0, 70)}…
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                  gap: '2px', flexShrink: 0 }}>
                  {rec.model && (
                    <span style={{ fontSize: '0.7rem', color: '#718096',
                      background: '#edf2f7', padding: '0 6px', borderRadius: '8px' }}>
                      {rec.model}
                    </span>
                  )}
                  <span style={{ fontSize: '0.75rem', color: '#a0aec0', whiteSpace: 'nowrap' }}>
                    {i === 0 ? 'Latest • ' : ''}{fmt(rec.created_at)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const _CLOUD_MODELS = new Set(['gemma4:31b', 'nemotron-3-ultra', 'deepseek-v4-flash', 'deepseek-v4-pro', 'glm-5', 'kimi-k2.5', 'minimax-m3']);
const formatModelLabel = (model) => {
  if (!model || model === 'local') return { label: 'local', cloud: false };
  const cloud = _CLOUD_MODELS.has(model) || model.endsWith(':cloud');
  const shortName = model.replace(':latest', '').replace(':cloud', '');
  return { label: shortName, cloud };
};

// Status badge pill: normal=green, abnormal=red, borderline=amber
const StatusBadge = ({ status }) => {
  const styles = {
    normal: { bg: '#f0fff4', color: '#22543d', label: 'Normal' },
    abnormal: { bg: '#fff5f5', color: '#c53030', label: 'Abnormal' },
    borderline: { bg: '#fffbeb', color: '#92400e', label: 'Borderline' }
  }[status] || { bg: '#edf2f7', color: '#4a5568', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '10px',
      fontSize: '0.75rem', fontWeight: '600',
      backgroundColor: styles.bg, color: styles.color
    }}>
      {styles.label}
    </span>
  );
};

// Direction of an abnormal lab value vs its reference range: 'high', 'low', or null
const abnormalDirection = (value, referenceRange) => {
  const val = parseFloat(value);
  if (!isFinite(val) || !referenceRange) return null;
  const nums = String(referenceRange).match(/-?\d+(\.\d+)?/g);
  if (!nums || nums.length === 0) return null;
  if (nums.length >= 2) {
    const [low, high] = [parseFloat(nums[0]), parseFloat(nums[1])];
    if (val > high) return 'high';
    if (val < low) return 'low';
    return null;
  }
  const bound = parseFloat(nums[0]);
  if (/[<≤]/.test(referenceRange)) return val > bound ? 'high' : null;
  if (/[>≥]/.test(referenceRange)) return val < bound ? 'low' : null;
  return null;
};

// CSV escape helper
const escapeCSV = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Lab Results Tab Component (moved outside)
const LabResultsTab = React.memo(({
  labResults,
  labSearch,
  setLabSearch,
  labDateRange,
  setLabDateRange,
  labCategory,
  setLabCategory,
  labSortDir,
  setLabSortDir,
  expandedTrend,
  toggleTrendChart,
  trendData,
  normalizeCategory,
  cleanTestName,
  patient,
  setActiveTab,
  expandedInterpretation,
  loadingInterpretation,
  fetchInterpretation,
  allInterpretations,
  setAllInterpretations,
  headers,
  allPatientTags,
  labTagFilter,
  setLabTagFilter,
  documents
}) => {
  // Compute available categories
  const availableCategories = useMemo(() =>
    [...new Set(labResults.map(r => normalizeCategory(r.category) || 'General'))].sort(),
    [labResults]
  );

  // Compute document IDs that match the selected tag (if any)
  const tagFilteredDocIds = useMemo(() => {
    if (!labTagFilter) return null;
    return documents
      .filter(d => d.tags && d.tags.some(t => t.id === labTagFilter.id))
      .map(d => d.id);
  }, [documents, labTagFilter]);

  const filteredLabs = useMemo(() => {
    let result = labResults.filter(r => {
      const matchSearch = !labSearch ||
        r.test_name.toLowerCase().includes(labSearch.toLowerCase()) ||
        (r.category || '').toLowerCase().includes(labSearch.toLowerCase());
      const labFrom = labDateRange[0];
      const labTo = labDateRange[1];
      const matchFrom = !labFrom || dayjs(r.report_date).isSameOrAfter(labFrom, 'day');
      const matchTo = !labTo || dayjs(r.report_date).isSameOrBefore(labTo, 'day');
      const matchCategory = !labCategory || normalizeCategory(r.category) === labCategory;
      const matchTag = !tagFilteredDocIds || tagFilteredDocIds.includes(r.document_id);
      return matchSearch && matchFrom && matchTo && matchCategory && matchTag;
    });

    // Sort categories alphabetically
    const grouped = result.reduce((acc, lab) => {
      const cat = normalizeCategory(lab.category) || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(lab);
      return acc;
    }, {});

    const sortedEntries = Object.entries(grouped).sort(([catA], [catB]) => {
      return labSortDir === 'asc' ? catA.localeCompare(catB) : catB.localeCompare(catA);
    });

    return sortedEntries.flatMap(([_, labs]) => labs);
  }, [labResults, labSearch, labDateRange, labCategory, labSortDir, normalizeCategory, tagFilteredDocIds]);

  const exportLabsCSV = () => {
    const headers = ['Test','Value','Unit','Reference Range','Date','Lab','Status'];
    const rows = filteredLabs.map(r => [
      escapeCSV(r.test_name), escapeCSV(r.value), escapeCSV(r.unit),
      escapeCSV(r.reference_range), escapeCSV(r.report_date),
      escapeCSV(r.hospital_name), r.is_abnormal ? 'Abnormal' : 'Normal'
    ]);
    const csv = [headers.map(escapeCSV).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lab_results_${patient?.first_name || 'patient'}.csv`;
    a.click();
  };

  const groupedLabs = useMemo(() => {
    const groups = {};
    filteredLabs.forEach(lab => {
      const cat = normalizeCategory(lab.category) || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(lab);
    });
    return groups;
  }, [filteredLabs, normalizeCategory]);

  return (
    <div>
      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search test or category..."
          value={labSearch}
          onChange={e => setLabSearch(e.target.value)}
          style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', minWidth: '220px' }}
        />
        <RangePicker
          value={labDateRange}
          onChange={(dates) => setLabDateRange(dates || [null, null])}
          format="DD-MMM-YYYY"
          placeholder={['From date', 'To date']}
          allowClear
          size="middle"
          popupStyle={{ zIndex: 1050 }}
          style={{ borderRadius: '8px', width: '280px' }}
          presets={[
            { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
            { label: 'Last 3 months', value: [dayjs().subtract(3, 'month'), dayjs()] },
            { label: 'Last 6 months', value: [dayjs().subtract(6, 'month'), dayjs()] },
            { label: 'Last 1 year', value: [dayjs().subtract(1, 'year'), dayjs()] },
            { label: 'This year', value: [dayjs().startOf('year'), dayjs()] },
          ]}
        />
        <select
          value={labCategory}
          onChange={e => setLabCategory(e.target.value)}
          style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }}
        >
          <option value="">All Categories</option>
          {availableCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button
          onClick={() => { setLabSearch(''); setLabDateRange([null, null]); setLabCategory(''); }}
          style={{ padding: '10px 20px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
        >
          Clear Filters
        </button>
        {/* Tag filter */}
        {allPatientTags.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
            <span style={{ fontSize: '0.85rem', color: '#718096' }}>Tag:</span>
            <button
              onClick={() => setLabTagFilter(null)}
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: !labTagFilter ? '#2b6cb0' : 'white',
                color: !labTagFilter ? 'white' : '#4a5568',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              All
            </button>
            {allPatientTags.map(tag => (
              <button
                key={tag.id}
                onClick={() => setLabTagFilter(
                  labTagFilter?.id === tag.id ? null : tag
                )}
                style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  border: `1px solid ${tag.color}`,
                  background: labTagFilter?.id === tag.id ? tag.color : tag.color + '15',
                  color: labTagFilter?.id === tag.id ? 'white' : tag.color,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '500'
                }}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={exportLabsCSV}
          style={{ padding: '10px 20px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
        >
          📥 Export CSV
        </button>
        <button
          onClick={() => setLabSortDir(d => d === 'asc' ? 'desc' : 'asc')}
          style={{ padding: '10px 16px', border: '2px solid #e2e8f0', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '0.9rem' }}
        >
          Categories {labSortDir === 'asc' ? 'A→Z ↑' : 'Z→A ↓'}
        </button>
      </div>

      {/* Lab Results Grouped by Category */}
      {Object.entries(groupedLabs).sort(([catA], [catB]) => {
        return labSortDir === 'asc' ? catA.localeCompare(catB) : catB.localeCompare(catA);
      }).map(([category, labs]) => (
        <div key={category} style={{ marginBottom: '24px' }}>
          <h5 style={{
            borderBottom: '2px solid #e2e8f0', paddingBottom: '8px', marginBottom: '12px',
            textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4a5568',
            fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            {category}
            <span style={{
              background: '#edf2f7', color: '#4a5568', borderRadius: '10px',
              padding: '1px 8px', fontSize: '0.72rem', fontWeight: '600', letterSpacing: 'normal'
            }}>{labs.length}</span>
          </h5>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Test</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Value</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Unit</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Ref Range</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Lab</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {labs.map(lab => (
                  <React.Fragment key={lab.id}>
                    <tr
                      style={{ borderBottom: '1px solid #e2e8f0', background: lab.is_abnormal ? '#fff5f5' : 'white', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.background = lab.is_abnormal ? '#fff5f5' : 'white'}
                    >
                      <td style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span>{cleanTestName(lab.test_name, lab.category)}</span>
                        {isFinite(lab.value) && labResults.filter(l => l.test_name === lab.test_name).length > 1 && (
                          <button
                            onClick={() => toggleTrendChart(lab.test_name)}
                            title={expandedTrend === lab.test_name ? 'Hide trend' : 'Show trend'}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1rem' }}
                          >
                            {expandedTrend === lab.test_name ? '📉' : '📈'}
                          </button>
                        )}
                        {lab.is_abnormal && (
                          <button
                            onClick={() => fetchInterpretation(lab)}
                            style={{
                              padding: '0',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.78rem',
                              color: '#2b6cb0',
                              textDecoration: 'underline',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {loadingInterpretation[lab.id] ? 'Loading...' :
                             expandedInterpretation[lab.id] !== undefined ? 'Hide' : 'What does this mean?'}
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '12px', color: lab.is_abnormal ? '#e53e3e' : '#2d3748', fontWeight: lab.is_abnormal ? '700' : 'normal' }}>
                        {lab.is_abnormal && abnormalDirection(lab.value, lab.reference_range) === 'high' && <span style={{ fontSize: '0.75rem', marginRight: '2px' }}>▲</span>}
                        {lab.is_abnormal && abnormalDirection(lab.value, lab.reference_range) === 'low' && <span style={{ fontSize: '0.75rem', marginRight: '2px' }}>▼</span>}
                        {lab.value}
                      </td>
                      <td style={{ padding: '12px', color: '#718096' }}>{lab.unit}</td>
                      <td style={{ padding: '12px', color: '#718096', fontSize: '0.85rem' }}>{lab.reference_range}</td>
                      <td style={{ padding: '12px' }}>{new Date(lab.report_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td style={{ padding: '12px', color: '#718096' }}>{lab.hospital_name || '-'}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <StatusBadge status={lab.is_abnormal ? 'abnormal' : 'normal'} />
                      </td>
                    </tr>
                    {expandedInterpretation[lab.id] !== undefined && (
                      <tr key={`interp-${lab.id}`}>
                        <td colSpan={7} style={{ padding: '0', background: 'none' }}>
                          {loadingInterpretation[lab.id] ? (
                            <div style={{ padding: '12px 16px', color: '#718096', fontSize: '0.85rem' }}>
                              Loading interpretation...
                            </div>
                          ) : expandedInterpretation[lab.id] ? (
                            <div style={{
                              margin: '0 0 8px 0',
                              padding: '16px 20px',
                              background: '#fffbeb',
                              borderLeft: '3px solid #f6ad55',
                              borderRadius: '0 0 8px 8px'
                            }}>
                              <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '12px', fontSize: '0.9rem' }}>
                                {expandedInterpretation[lab.id].interpretation}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem' }}>
                                {expandedInterpretation[lab.id].dietary_advice && (
                                  <div style={{ paddingLeft: '10px', borderLeft: '3px solid #38a169' }}>
                                    <div style={{ fontWeight: '600', color: '#22543d', marginBottom: '4px' }}>Diet</div>
                                    <div style={{ color: '#4a5568', lineHeight: '1.6' }}>{expandedInterpretation[lab.id].dietary_advice}</div>
                                  </div>
                                )}
                                {expandedInterpretation[lab.id].lifestyle_advice && (
                                  <div style={{ paddingLeft: '10px', borderLeft: '3px solid #2b6cb0' }}>
                                    <div style={{ fontWeight: '600', color: '#2c5282', marginBottom: '4px' }}>Lifestyle</div>
                                    <div style={{ color: '#4a5568', lineHeight: '1.6' }}>{expandedInterpretation[lab.id].lifestyle_advice}</div>
                                  </div>
                                )}
                                {expandedInterpretation[lab.id].followup_tests && (
                                  <div style={{ paddingLeft: '10px', borderLeft: '3px solid #7c3aed' }}>
                                    <div style={{ fontWeight: '600', color: '#553c9a', marginBottom: '4px' }}>Follow-up tests</div>
                                    <div style={{ color: '#4a5568', lineHeight: '1.6' }}>{expandedInterpretation[lab.id].followup_tests}</div>
                                  </div>
                                )}
                                {expandedInterpretation[lab.id].see_doctor_if && (
                                  <div style={{ paddingLeft: '10px', borderLeft: '3px solid #e53e3e' }}>
                                    <div style={{ fontWeight: '600', color: '#c53030', marginBottom: '4px' }}>See doctor if</div>
                                    <div style={{ color: '#742a2a', lineHeight: '1.6' }}>{expandedInterpretation[lab.id].see_doctor_if}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div style={{ padding: '12px 16px', color: '#718096', fontSize: '0.85rem', fontStyle: 'italic' }}>
                              No interpretation available for this test yet.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Expanded Trend Chart */}
          {labs.some(l => expandedTrend === l.test_name) && (
            <div style={{ marginTop: '20px', padding: '20px', background: '#f7fafc', borderRadius: '8px' }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData[expandedTrend] || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#718096" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#718096" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#2b6cb0" strokeWidth={2} dot={{ r: 4 }} name={expandedTrend} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ))}

      {filteredLabs.length === 0 && (
        <p style={{ textAlign: 'center', color: '#718096', padding: '40px' }}>No lab results match your filters.</p>
      )}
    </div>
  );
});

const HealthSummaryTab = React.memo(({
  patient, labResults, vitals, interpretations, headers, setAllInterpretations
}) => {

  const getAge = () => {
    if (!patient?.date_of_birth) return '?';
    return Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000));
  };

  // Get all abnormal lab results - deduplicate by test_name keeping latest
  const abnormalLabs = React.useMemo(() => {
    const abnormal = labResults.filter(r => r.is_abnormal);
    // Keep latest entry per test_name
    const latestMap = {};
    abnormal.forEach(r => {
      const key = r.test_name;
      if (!latestMap[key] || new Date(r.report_date) > new Date(latestMap[key].report_date)) {
        latestMap[key] = r;
      }
    });
    return Object.values(latestMap).sort((a, b) =>
      (a.category || '').localeCompare(b.category || '')
    );
  }, [labResults]);

  // Get abnormal vitals - latest per type
  const abnormalVitals = React.useMemo(() => {
    const result = [];

    const getLatest = (type, readingType = null) => {
      return [...vitals]
        .filter(v => v.vital_type === type && (!readingType || v.reading_type === readingType))
        .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
    };

    const bp = getLatest('blood_pressure');
    if (bp && (bp.systolic > 130 || bp.diastolic > 80)) {
      result.push({
        test: 'Blood Pressure',
        value: `${bp.systolic}/${bp.diastolic}`,
        unit: 'mmHg',
        reference: '90-120 / 60-80',
        date: bp.recorded_at,
        advice: 'Reduce salt intake, exercise regularly, manage stress, monitor daily'
      });
    }

    const fasting = getLatest('blood_sugar', 'fasting');
    if (fasting) {
      const val = parseFloat(fasting.sugar_value || fasting.value);
      if (val > 100) result.push({
        test: 'Fasting Blood Sugar',
        value: val,
        unit: 'mg/dL',
        reference: '70-100',
        date: fasting.recorded_at,
        advice: 'Avoid sugar and refined carbs, walk 30 min daily, eat smaller meals'
      });
    }

    const pp = getLatest('blood_sugar', 'pp');
    if (pp) {
      const val = parseFloat(pp.sugar_value || pp.value);
      if (val >= 140) result.push({
        test: 'PP Blood Sugar',
        value: val,
        unit: 'mg/dL',
        reference: '<140',
        date: pp.recorded_at,
        advice: 'Walk 15 min after meals, reduce carb portions, include protein in every meal'
      });
    }

    const hba1c = getLatest('blood_sugar', 'hba1c');
    if (hba1c) {
      const val = parseFloat(hba1c.sugar_value || hba1c.value);
      if (val >= 5.7) result.push({
        test: 'HbA1c',
        value: val,
        unit: '%',
        reference: '<5.7',
        date: hba1c.recorded_at,
        advice: 'Control daily blood sugar, exercise regularly, reduce simple carbohydrates'
      });
    }

    const hr = getLatest('heart_rate');
    if (hr) {
      const val = parseInt(hr.hr_value || hr.value);
      if (val < 60 || val > 100) result.push({
        test: 'Heart Rate',
        value: val,
        unit: 'bpm',
        reference: '60-100',
        date: hr.recorded_at,
        advice: val > 100 ? 'Reduce caffeine, manage stress, consult doctor' : 'Consult doctor for evaluation'
      });
    }

    const spo2 = getLatest('spo2');
    if (spo2) {
      const val = parseFloat(spo2.spo2_value || spo2.value);
      if (val < 95) result.push({
        test: 'SpO2',
        value: val,
        unit: '%',
        reference: '≥95',
        date: spo2.recorded_at,
        advice: 'Seek immediate medical attention if consistently below 95%'
      });
    }

    return result;
  }, [vitals]);

  // Pre-fetch all abnormal lab interpretations using shared state
  React.useEffect(() => {
    const fetchAll = async () => {
      const results = {};
      for (const lab of abnormalLabs) {
        try {
          const detectCondition = (lab) => {
            if (!lab.is_abnormal) return 'normal';
            const value = parseFloat(lab.value);
            const ref = (lab.reference_range || '').trim();
            if (isNaN(value)) {
              const valLower = String(lab.value).toLowerCase();
              if (valLower === 'trace') {
                return 'high';
              }
              if (['positive','present','detected','reactive'].includes(valLower)) {
                return 'positive';
              }
              return 'abnormal';
            } else if (ref.match(/<\s*[\d.]+\s*(?:normal|desirable|optimal)/i)) {
              const m = ref.match(/<\s*([\d.]+)/);
              return m && value >= parseFloat(m[1]) ? 'high' : 'low';
            } else if (ref.startsWith('<')) {
              const threshold = parseFloat(ref.replace(/[<>=\s]/g,'').split(/[^0-9.]/)[0]);
              return value >= threshold ? 'high' : 'low';
            } else if (ref.startsWith('>')) {
              const threshold = parseFloat(ref.replace(/[<>=\s]/g,'').split(/[^0-9.]/)[0]);
              return value <= threshold ? 'low' : 'high';
            } else {
              const rangeMatch = ref.match(/^(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
              if (rangeMatch) {
                const mid = (parseFloat(rangeMatch[1]) + parseFloat(rangeMatch[2])) / 2;
                return value > mid ? 'high' : 'low';
              }
            }
            return 'high';
          };
          const condition = detectCondition(lab);
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
          const res = await fetch(
            `${API_URL}/interpretations/lookup?test_name=${encodeURIComponent(lab.test_name)}&condition=${condition}`,
            { headers: headers }
          );
          const data = await res.json();
          if (data.interpretation) {
            results[lab.id] = data.interpretation;
          }
        } catch(e) {
          console.error('Failed to fetch interpretation for', lab.test_name, e);
        }
      }
      // Update local results and also shared state
      if (setAllInterpretations) {
        setAllInterpretations(prev => ({ ...prev, ...results }));
      }
    };
    if (abnormalLabs.length > 0) fetchAll();
  }, [abnormalLabs, headers, setAllInterpretations]);

  const exportCSV = () => {
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = [];

    // Header
    rows.push([
      'Health Summary Report',
      `Patient: ${patient?.first_name} ${patient?.last_name}`,
      `Age: ${getAge()}`,
      `Gender: ${patient?.gender || ''}`,
      `Generated: ${new Date().toLocaleDateString('en-IN')}`
    ].join(','));
    rows.push('');

    // Abnormal Vitals section
    if (abnormalVitals.length > 0) {
      rows.push('ABNORMAL VITALS');
      rows.push(['Test', 'Value', 'Unit', 'Normal Range', 'Date', 'Advice'].join(','));
      abnormalVitals.forEach(v => {
        rows.push([
          escapeCSV(v.test),
          escapeCSV(v.value),
          escapeCSV(v.unit),
          escapeCSV(v.reference),
          escapeCSV(new Date(v.date).toLocaleDateString('en-IN')),
          escapeCSV(v.advice)
        ].join(','));
      });
      rows.push('');
    }

    // Abnormal Lab Results section
    if (abnormalLabs.length > 0) {
      rows.push('ABNORMAL LAB RESULTS');
      rows.push(['Test', 'Category', 'Value', 'Unit', 'Normal Range', 'Date', 'Lab', 'Advice'].join(','));
      abnormalLabs.forEach(lab => {
        const interp = interpretations[lab.id];
        const advice = interp
          ? `${interp.dietary_advice || ''} ${interp.lifestyle_advice ? ' ' + interp.lifestyle_advice : ''}`.trim()
          : '';
        rows.push([
          escapeCSV(lab.test_name),
          escapeCSV(lab.category),
          escapeCSV(lab.value),
          escapeCSV(lab.unit),
          escapeCSV(lab.reference_range),
          escapeCSV(lab.report_date),
          escapeCSV(lab.hospital_name),
          escapeCSV(advice)
        ].join(','));
      });
    }

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Health_Summary_${patient?.first_name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalAbnormal = abnormalLabs.length + abnormalVitals.length;

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, color: '#2d3748' }}>📋 Abnormal Results</h3>
          <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.85rem' }}>
            {patient?.first_name} {patient?.last_name} • {getAge()} yrs • {patient?.gender} • Blood Type: {patient?.blood_type || 'Not set'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#718096' }}>
            {totalAbnormal} abnormal finding{totalAbnormal !== 1 ? 's' : ''}
          </span>
          <button
            onClick={exportCSV}
            style={{
              padding: '8px 16px',
              background: '#38a169',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📥 Download CSV
          </button>
        </div>
      </div>

      {totalAbnormal === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
          <h3>All results are within normal range!</h3>
          <p>Keep up the healthy lifestyle.</p>
        </div>
      ) : (
        <>
          {/* Abnormal Vitals */}
          {abnormalVitals.length > 0 && (
            <div style={{ marginBottom: '28px' }}>
              <h4 style={{ color: '#2d3748', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💓 Abnormal Vitals
                <span style={{ fontSize: '0.8rem', background: '#fed7d7', color: '#c53030', padding: '2px 8px', borderRadius: '12px' }}>
                  {abnormalVitals.length}
                </span>
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {[
                        { label: 'Test', width: '18%' },
                        { label: 'Reading', width: '12%' },
                        { label: 'Normal Range', width: '15%' },
                        { label: 'Date', width: '12%' },
                        { label: 'Advice', width: '43%' }
                      ].map(col => (
                        <th key={col.label} style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600', fontSize: '0.85rem', width: col.width }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {abnormalVitals.map((v, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: '#fff5f5' }}>
                        <td style={{ padding: '12px', fontWeight: '500', color: '#2d3748', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          <span style={{ marginRight: '6px' }}>⚠️</span>{v.test}
                        </td>
                        <td style={{ padding: '12px', color: '#e53e3e', fontWeight: '600', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {v.value} <span style={{ color: '#718096', fontWeight: '400', fontSize: '0.8rem' }}>{v.unit}</span>
                        </td>
                        <td style={{ padding: '12px', color: '#718096', fontSize: '0.85rem', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>{v.reference}</td>
                        <td style={{ padding: '12px', color: '#718096', fontSize: '0.85rem', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {new Date(v.date).toLocaleDateString('en-IN')}
                        </td>
                        <td style={{ padding: '12px', color: '#4a5568', fontSize: '0.85rem', maxWidth: '250px', lineHeight: '1.6', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {v.advice}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Abnormal Lab Results */}
          {abnormalLabs.length > 0 && (
            <div>
              <h3 style={{ color: '#1a202c', marginBottom: '16px', fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
                Abnormal Lab Results
                <span style={{ fontSize: '0.8rem', background: '#fed7d7', color: '#c53030', padding: '2px 10px', borderRadius: '12px', fontWeight: '600' }}>
                  {abnormalLabs.length}
                </span>
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {[
                        { label: 'Test', width: '20%' },
                        { label: 'Value', width: '10%' },
                        { label: 'Normal Range', width: '15%' },
                        { label: 'Advice', width: '55%' }
                      ].map(col => (
                        <th key={col.label} style={{ padding: '12px', textAlign: 'left', color: '#4a5568', fontWeight: '600', fontSize: '0.85rem', width: col.width, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {abnormalLabs.map(lab => {
                      const interp = interpretations[lab.id];
                      return (
                        <tr key={lab.id} style={{ borderBottom: '1px solid #e2e8f0', background: '#fff5f5', borderLeft: '3px solid #e53e3e' }}>
                          <td style={{ padding: '16px 12px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            <div style={{ fontWeight: '600', color: '#2d3748' }}>
                              {lab.test_name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '2px' }}>
                              {lab.category} • {lab.report_date} • {lab.hospital_name}
                            </div>
                          </td>
                          <td style={{ padding: '16px 12px', color: '#e53e3e', fontWeight: '700', whiteSpace: 'nowrap', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {lab.value} <span style={{ color: '#718096', fontWeight: '400', fontSize: '0.8rem' }}>{lab.unit}</span>
                          </td>
                          <td style={{ padding: '16px 12px', color: '#718096', fontSize: '0.85rem', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                            {lab.reference_range}
                          </td>
                          <td style={{ padding: '16px 12px', color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.7', maxWidth: '380px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                            {interp ? (
                              <div>
                                <div style={{ color: '#92400e', fontWeight: '600', marginBottom: '10px', fontSize: '0.9rem', lineHeight: '1.6' }}>
                                  {interp.interpretation}
                                </div>
                                {interp.dietary_advice && (
                                  <div style={{ marginBottom: '8px', paddingLeft: '10px', borderLeft: '3px solid #38a169' }}>
                                    <span style={{ fontWeight: '600', color: '#22543d' }}>Diet: </span>
                                    {interp.dietary_advice}
                                  </div>
                                )}
                                {interp.lifestyle_advice && (
                                  <div style={{ marginBottom: '8px', paddingLeft: '10px', borderLeft: '3px solid #2b6cb0' }}>
                                    <span style={{ fontWeight: '600', color: '#2c5282' }}>Lifestyle: </span>
                                    {interp.lifestyle_advice}
                                  </div>
                                )}
                                {interp.followup_tests && (
                                  <div style={{ marginBottom: '8px', paddingLeft: '10px', borderLeft: '3px solid #7c3aed' }}>
                                    <span style={{ fontWeight: '600', color: '#553c9a' }}>Follow-up: </span>
                                    {interp.followup_tests}
                                  </div>
                                )}
                                {interp.see_doctor_if && (
                                  <div style={{ color: '#c53030', paddingLeft: '10px', borderLeft: '3px solid #e53e3e' }}>
                                    <span style={{ fontWeight: '600' }}>See doctor if: </span>
                                    {interp.see_doctor_if}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>
                                No advice available
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

function PatientDashboard({ user }) {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [vitals, setVitals] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [labResults, setLabResults] = useState([]);
  const [radiologyReports, setRadiologyReports] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedTrend, setExpandedTrend] = useState(null); // test name for expanded chart
  const ITEMS_PER_PAGE = 25;

  // Tab navigation
  const [activeTab, setActiveTab] = useState('overview');

  // AI Recommendations
  const [aiRecommendation, setAiRecommendation] = useState('');
  const [aiPending, setAiPending] = useState(false);    // waiting for background job
  const [aiGenerated, setAiGenerated] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [aiModel, setAiModel] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiCacheLoaded, setAiCacheLoaded] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [showAiHistory, setShowAiHistory] = useState(false);

  // "Ask Your Doctor" questions
  const [doctorQuestions, setDoctorQuestions] = useState('');
  const [dqPending, setDqPending] = useState(false);    // waiting for background job
  const [dqGenerated, setDqGenerated] = useState(false);
  const [dqUpdated, setDqUpdated] = useState(null);
  const [dqModel, setDqModel] = useState(null);
  const [dqHistory, setDqHistory] = useState([]);
  const [showDqHistory, setShowDqHistory] = useState(false);

  // Polling ref: cleared on patient switch or when both jobs are settled
  const pollTimerRef = useRef(null);
  const patientIdRef = useRef(patientId);
  patientIdRef.current = patientId;

  // Family sidebar
  const [familyPatients, setFamilyPatients] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('hrv_sidebar_collapsed');
      if (saved !== null) return saved === '1';
      return window.innerWidth < 900;
    } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      try { localStorage.setItem('hrv_sidebar_collapsed', prev ? '0' : '1'); } catch {}
      return !prev;
    });
  };

  // Lab filters
  const [labSearch, setLabSearch] = useState('');
  const [labDateRange, setLabDateRange] = useState([null, null]);
  const [labCategory, setLabCategory] = useState('');
  const [labSortField, setLabSortField] = useState('report_date');
  const [labSortDir, setLabSortDir] = useState('desc');

  // Interpretation expansion states
  const [expandedInterpretation, setExpandedInterpretation] = useState({});
  const [loadingInterpretation, setLoadingInterpretation] = useState({});
  const [allInterpretations, setAllInterpretations] = useState({});

  // Vitals filters
  const [vitalSearch, setVitalSearch] = useState('');
  const [vitalDateRange, setVitalDateRange] = useState([null, null]);
  const [vitalSortField, setVitalSortField] = useState('recorded_at');
  const [vitalSortDir, setVitalSortDir] = useState('desc');

  // Document filter
  const [docFilter, setDocFilter] = useState('all');

  // Tag filters
  const [allPatientTags, setAllPatientTags] = useState([]);
  const [selectedFilterTag, setSelectedFilterTag] = useState(null);
  const [labTagFilter, setLabTagFilter] = useState(null);
  const [vitalTagFilter, setVitalTagFilter] = useState(null);

  // Pagination for filtered vitals (separate tab-specific pagination)
  const [vitalsPage, setVitalsPage] = useState(1);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Detect condition based on value and reference range
  const detectCondition = (lab) => {
    if (!lab.is_abnormal) return 'normal';

    const value = parseFloat(lab.value);
    const ref = (lab.reference_range || '').trim();

    // Narrative values
    if (isNaN(value)) {
      const valLower = String(lab.value).toLowerCase();
      if (valLower === 'trace') {
        return 'high';
      }
      if (['positive', 'present', 'detected', 'reactive'].includes(valLower)) {
        return 'positive';
      }
      return 'abnormal';
    }

    // Complex multi-part ranges like "<150 Desirable; 150-199 Borderline"
    // Extract the FIRST threshold which is typically the normal upper limit
    // Pattern: "<X Normal" or "<X Desirable" or "<X Optimal"
    const ltNormal = ref.match(/<\s*(\d+\.?\d*)\s*(?:normal|desirable|optimal|good)/i);
    if (ltNormal) {
      return value >= parseFloat(ltNormal[1]) ? 'high' : 'low';
    }

    // Pattern: ">X Normal" or ">=X Normal"
    const gtNormal = ref.match(/>=?\s*(\d+\.?\d*)\s*(?:normal|desirable|optimal|good)/i);
    if (gtNormal) {
      return value <= parseFloat(gtNormal[1]) ? 'low' : 'high';
    }

    // Simple "<X" range
    if (ref.match(/^<\s*[\d.]+/)) {
      const threshold = parseFloat(ref.replace(/[^0-9.]/g, ''));
      return value >= threshold ? 'high' : 'low';
    }

    // Simple ">X" range
    if (ref.match(/^>=?\s*[\d.]+/)) {
      const threshold = parseFloat(ref.replace(/[^0-9.]/g, ''));
      return value <= threshold ? 'low' : 'high';
    }

    // Simple "X-Y" range
    const rangeMatch = ref.match(/^(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
    if (rangeMatch) {
      const lo = parseFloat(rangeMatch[1]);
      const hi = parseFloat(rangeMatch[2]);
      const mid = (lo + hi) / 2;
      return value > mid ? 'high' : 'low';
    }

    // Multi-part range - find first numeric range as baseline
    const firstRange = ref.match(/(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
    if (firstRange) {
      const lo = parseFloat(firstRange[1]);
      const hi = parseFloat(firstRange[2]);
      const mid = (lo + hi) / 2;
      return value > mid ? 'high' : 'low';
    }

    // First < threshold found anywhere
    const anyLt = ref.match(/<\s*(\d+\.?\d*)/);
    if (anyLt) {
      return value >= parseFloat(anyLt[1]) ? 'high' : 'low';
    }

    return 'high'; // default
  };

  // Fetch medical interpretation for abnormal lab result
  const fetchInterpretation = async (lab) => {
    if (!lab.is_abnormal) return;

    const key = lab.id;
    if (expandedInterpretation[key] !== undefined) {
      // Toggle off if already expanded
      setExpandedInterpretation(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    setLoadingInterpretation(prev => ({ ...prev, [key]: true }));

    const condition = detectCondition(lab);

    // Debug logging
    console.log('Fetching interpretation for:', lab.test_name, 'condition:', condition, 'value:', lab.value, 'ref:', lab.reference_range);

    try {
      const res = await api.get(
        `${API_URL}/interpretations/lookup?test_name=${encodeURIComponent(lab.test_name)}&condition=${condition}`
      );
      const interpretation = res.data.interpretation || null;
      setExpandedInterpretation(prev => ({
        ...prev,
        [key]: interpretation
      }));
      // Also update shared allInterpretations state
      if (interpretation) {
        setAllInterpretations(prev => ({ ...prev, [key]: interpretation }));
      }
    } catch (error) {
      console.error('Failed to fetch interpretation:', error);
      setExpandedInterpretation(prev => ({ ...prev, [key]: null }));
    } finally {
      setLoadingInterpretation(prev => ({ ...prev, [key]: false }));
    }
  };

  // Pagination calculations (must be at top level)
  const totalPages = Math.ceil(vitals.length / ITEMS_PER_PAGE);
  const paginatedVitals = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return vitals.slice(start, end);
  }, [vitals, currentPage]);

  // Reset to page 1 when vitals change
  useEffect(() => {
    setCurrentPage(1);
  }, [vitals.length]);

  // Compute trend data for lab results
  const trendData = useMemo(() => {
    const acc = {};
    labResults.forEach(r => {
      if (!acc[r.test_name]) acc[r.test_name] = [];
      // Only include if we have a numeric value and date
      const value = parseFloat(r.value);
      if (!isNaN(value) && r.report_date) {
        acc[r.test_name].push({
          date: r.report_date,
          value: value,
          hospital: r.hospital_name || '-'
        });
      }
    });
    // Sort each test's data by date
    Object.keys(acc).forEach(testName => {
      acc[testName].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    return acc;
  }, [labResults]);

  const toggleTrendChart = (testName) => {
    setExpandedTrend(expandedTrend === testName ? null : testName);
  };

  // Normalize category names to prevent duplicates
  const normalizeCategory = (cat) => {
    if (!cat) return 'General';
    const normalized = cat.trim()
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());

    const categoryMap = {
      'Hematology': 'Hematology',
      'Haematology': 'Hematology',
      'Liver Function': 'Liver Function (LFT)',
      'Liver Function Test': 'Liver Function (LFT)',
      'Lft': 'Liver Function (LFT)',
      'Kidney Function': 'Kidney Function (KFT)',
      'Kidney/Renal Function': 'Kidney Function (KFT)',
      'Renal Function': 'Kidney Function (KFT)',
      'Kft': 'Kidney Function (KFT)',
      'Lipid Profile': 'Lipid Profile',
      'Cholesterol': 'Lipid Profile',
      'Thyroid Profile': 'Thyroid Profile',
      'Thyroid': 'Thyroid Profile',
      'Glucose': 'Glucose / Diabetes',
      'Diabetes': 'Glucose / Diabetes',
      'Vitamins': 'Vitamins',
      'Vitamin': 'Vitamins',
      'Electrolytes': 'Electrolytes',
      'Cardiac Marker': 'Cardiac Markers',
      'Cardiac Markers': 'Cardiac Markers',
      'Cardiac': 'Cardiac Markers',
      'Cancer Markers': 'Cancer Markers',
      'Cancer Marker': 'Cancer Markers',
      'Urine Examination': 'Urine Examination',
      'Urine': 'Urine Examination',
      'Iron Studies': 'Iron Studies',
      'Allergy': 'Allergy',
      'Autoimmune': 'Autoimmune',
      'Hormones': 'Hormones',
      'Pancreas': 'Pancreas',
    };
    return categoryMap[normalized] || normalized;
  };

  // Clean test name by removing redundant prefixes
  const cleanTestName = (testName, category) => {
    if (!testName || !category) return testName;
    const cat = category.toLowerCase();
    if (cat.includes('urine') && testName.toLowerCase().startsWith('urine ')) {
      return testName.substring(6); // Remove "Urine " prefix
    }
    return testName;
  };

  // Check if value is numeric
  const isNumeric = (val) => {
    if (val === null || val === undefined || val === '') return false;
    const num = parseFloat(val);
    return !isNaN(num) && isFinite(num);
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_URL}/patients/${patientId}`, { headers });
      setPatient(response.data.patient);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [patientId, headers]);

  const fetchVitals = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/vitals?patient_id=${patientId}`, { headers });
      setVitals(response.data.vitals || []);
    } catch (err) {
      console.error('Failed to load vitals:', err.response?.data?.error || err.message);
    }
  }, [patientId, headers]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/documents/${patientId}`, { headers });
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err.response?.data?.error || err.message);
      setDocuments([]);
    }
  }, [patientId, headers]);

  const fetchLabResults = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/documents/${patientId}/lab-results`, { headers });
      const labResultsData = response.data.lab_results || [];

      // Deduplicate by combining test_name + report_date + value as unique key
      const deduplicatedResults = labResultsData.filter((result, index, self) =>
        index === self.findIndex(r =>
          r.test_name === result.test_name &&
          r.report_date === result.report_date &&
          r.value === result.value
        )
      );

      setLabResults(deduplicatedResults);
    } catch (err) {
      console.error('Failed to fetch lab results:', err);
    }
  }, [patientId, headers]);

  const fetchRadiology = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/documents/${patientId}/radiology`, { headers });
      setRadiologyReports(response.data.radiology_reports || []);
    } catch (err) {
      console.error('Failed to fetch radiology:', err);
    }
  }, [patientId, headers]);

  const fetchPrescriptions = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/documents/${patientId}/prescriptions`, { headers });
      setPrescriptions(response.data.prescriptions || []);
    } catch (err) {
      console.error('Failed to fetch prescriptions:', err);
    }
  }, [patientId, headers]);

  const fetchPatientTags = useCallback(async () => {
    try {
      const response = await api.get(`${API_URL}/tags/patient/${patientId}`, { headers });
      setAllPatientTags(response.data.tags || []);
    } catch (err) {
      console.error('Failed to fetch patient tags:', err);
    }
  }, [patientId, headers]);

  // ── AI history: load on mount / patient switch, then poll while pending ──
  const applyAiHistory = useCallback((gens) => {
    const insights  = gens.filter(g => g.kind === 'insights');
    const questions = gens.filter(g => g.kind === 'doctor_questions');
    setAiHistory(insights);
    setDqHistory(questions);
    const latest    = insights[0];
    const latestDq  = questions[0];
    if (latest) {
      if (latest.status === 'done') {
        setAiRecommendation(latest.content);
        setAiGenerated(true);
        setLastUpdated(new Date(latest.created_at));
        setAiModel(latest.model || null);
        setAiPending(false);
      } else if (latest.status === 'failed') {
        setAiRecommendation(latest.content || 'Generation failed — please try again.');
        setAiGenerated(true);
        setAiModel(null);
        setAiPending(false);
      } else {
        setAiPending(true);  // still running
      }
    }
    if (latestDq) {
      if (latestDq.status === 'done') {
        setDoctorQuestions(latestDq.content);
        setDqGenerated(true);
        setDqUpdated(new Date(latestDq.created_at));
        setDqModel(latestDq.model || null);
        setDqPending(false);
      } else if (latestDq.status === 'failed') {
        setDoctorQuestions(latestDq.content || 'Generation failed — please try again.');
        setDqGenerated(true);
        setDqModel(null);
        setDqPending(false);
      } else {
        setDqPending(true);
      }
    }
    // Return whether anything is still pending
    const stillPending =
      (latest  && latest.status  === 'pending') ||
      (latestDq && latestDq.status === 'pending');
    return stillPending;
  }, []);

  useEffect(() => {
    if (!patientId) return;
    // Clear all AI state on patient switch
    setAiRecommendation('');
    setAiGenerated(false);
    setAiPending(false);
    setLastUpdated(null);
    setAiModel(null);
    setAiHistory([]);
    setShowAiHistory(false);
    setDoctorQuestions('');
    setDqGenerated(false);
    setDqPending(false);
    setDqUpdated(null);
    setDqModel(null);
    setDqHistory([]);
    setShowDqHistory(false);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`${API_URL}/ai/history/${patientId}`, { headers });
        if (cancelled) return;
        const gens = res.data.generations || [];
        const stillPending = applyAiHistory(gens);
        if (!stillPending && pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch (e) {
        console.error('Failed to poll AI generations:', e);
      }
    };

    const load = async () => {
      try {
        const res = await api.get(`${API_URL}/ai/history/${patientId}`, { headers });
        if (cancelled) return;
        const gens = res.data.generations || [];
        const stillPending = applyAiHistory(gens);
        // Start polling if any job is still running
        if (stillPending && !pollTimerRef.current) {
          pollTimerRef.current = setInterval(poll, 10000);
        }
      } catch (e) {
        console.error('Failed to load saved AI generations:', e);
      } finally {
        if (!cancelled) setAiCacheLoaded(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [patientId, headers, applyAiHistory]);

  // Load family members for sidebar
  useEffect(() => {
    const loadFamily = async () => {
      try {
        const response = await api.get(`${API_URL}/patients`, { headers });
        setFamilyPatients(response.data.patients || []);
      } catch (err) {
        console.error('Failed to load family members:', err);
      }
    };
    loadFamily();
  }, [headers]);


  // Load all data on mount
  useEffect(() => {
    const loadData = async () => {
      await fetchPatient();
      await fetchVitals();
      await fetchDocuments();
      await fetchLabResults();
      await fetchRadiology();
      await fetchPrescriptions();
      await fetchPatientTags();
    };
    loadData();
  }, [fetchPatient, fetchVitals, fetchDocuments, fetchLabResults, fetchRadiology, fetchPrescriptions, fetchPatientTags]);

  const handleDownloadPDF = async () => {
    try {
      // Include the saved "Ask Your Doctor" questions so they appear in the report
      const dq = dqGenerated ? (doctorQuestions || '') : '';
      const response = await fetch(
        `${API_URL}/reports/patient/${patientId}/pdf`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ doctor_questions: dq })
        }
      );
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HRV_${patient?.first_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      alert('Could not generate PDF. Please try again.');
      console.error(e);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this patient profile? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`${API_URL}/patients/${patientId}`, { headers });
      alert('Patient deleted successfully');
      navigate('/dashboard');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete patient');
    }
  };

  const handleDeleteVital = async (vitalId) => {
    if (!window.confirm('Delete this vital reading?')) {
      return;
    }

    try {
      await api.delete(`${API_URL}/vitals/${vitalId}`, { headers });
      alert('Vital deleted successfully');
      fetchVitals(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete vital');
    }
  };

  // Get latest vital of a specific type (sorted by date)
  const getLatestVital = (type) => {
    const sorted = [...vitals]
      .filter(v => v.vital_type === type)
      .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));
    return sorted.length > 0 ? sorted[0] : null;
  };

  // Get color based on vital type and value
  const getVitalColor = (vital) => {
    if (!vital) return '#cbd5e0';

    switch (vital.vital_type) {
      case 'blood_pressure':
        const bpStatus = (vital.systolic < 120 && vital.diastolic < 80) ? 'green' :
                        (vital.systolic >= 130 || vital.diastolic >= 80) ? 'red' : 'yellow';
        return bpStatus === 'green' ? '#48bb78' : bpStatus === 'yellow' ? '#ecc94b' : '#f56565';

      case 'blood_sugar':
        const isFasting = vital.reading_type === 'fasting';
        const sugarVal = parseFloat(vital.value);
        if (isFasting) {
          return (sugarVal >= 70 && sugarVal <= 100) ? '#48bb78' :
                 (sugarVal > 100 && sugarVal <= 125) ? '#ecc94b' : '#f56565';
        } else {
          return (sugarVal < 140) ? '#48bb78' :
                 (sugarVal >= 140 && sugarVal <= 199) ? '#ecc94b' : '#f56565';
        }

      case 'heart_rate':
        const hr = parseInt(vital.value);
        return (hr >= 60 && hr <= 100) ? '#48bb78' : '#ecc94b';

      case 'spo2':
        const spo2 = parseFloat(vital.value);
        return (spo2 >= 95) ? '#48bb78' : '#f56565';

      case 'temperature':
        const temp = parseFloat(vital.value);
        const unit = vital.unit || 'celsius';
        let tempC = temp;
        if (unit === 'fahrenheit') {
          tempC = (temp - 32) * 5/9;
        }
        return (tempC >= 36.1 && tempC <= 37.2) ? '#48bb78' : '#ecc94b';

      case 'weight':
        const bmi = parseFloat(vital.bmi);
        if (bmi && !isNaN(bmi)) {
          return (bmi >= 18.5 && bmi <= 24.9) ? '#48bb78' :
                 ((bmi >= 25 && bmi <= 29.9) || bmi < 18.5) ? '#ecc94b' : '#f56565';
        }
        return '#cbd5e0';

      default:
        return '#cbd5e0';
    }
  };

  // Format vital value (numeric value with optional labels) - for table column
  const formatVitalValueOnly = (vital) => {
    if (!vital) return '--';

    switch(vital.vital_type) {
      case 'blood_pressure': {
        const sys = vital.systolic || vital.value;
        return `${sys}/${vital.diastolic}`;
      }
      case 'blood_sugar': {
        const sugarVal = parseFloat(vital.sugar_value || vital.value);
        if (isNaN(sugarVal)) return '--';
        if (vital.reading_type === 'hba1c') return sugarVal;
        const label = vital.reading_type === 'fasting' ? 'Fasting' :
                      vital.reading_type === 'pp' ? 'PP' : vital.reading_type || '';
        return `${sugarVal}${label ? ` (${label})` : ''}`;
      }
      case 'weight': {
        const wtVal = parseFloat(vital.weight_value || vital.value);
        if (isNaN(wtVal)) return '--';
        return `${wtVal}${vital.bmi ? ` | BMI: ${parseFloat(vital.bmi).toFixed(1)}` : ''}`;
      }
      case 'heart_rate': {
        const hrVal = vital.hr_value || vital.value;
        return hrVal || '--';
      }
      case 'spo2': {
        const spo2Val = vital.spo2_value || vital.value;
        return spo2Val || '--';
      }
      case 'temperature': {
        const tempVal = vital.temp_value || vital.value;
        return tempVal || '--';
      }
      default: return '-';
    }
  };

  // Format vital unit - for separate unit column
  const formatVitalUnit = (vital) => {
    if (!vital) return '';

    switch(vital.vital_type) {
      case 'blood_pressure': return 'mmHg';
      case 'blood_sugar':
        return vital.reading_type === 'hba1c' ? '%' : 'mg/dL';
      case 'weight': return 'kg';
      case 'heart_rate': return 'bpm';
      case 'spo2': return '%';
      case 'temperature':
        return vital.temp_unit === 'fahrenheit' ? '°F' : '°C';
      default: return '';
    }
  };

  // Get vital status emoji (✅ or ⚠️)
  const getVitalStatus = (vital) => {
    if (!vital) return 'normal';
    switch(vital.vital_type) {
      case 'blood_pressure':
        return (vital.systolic <= 120 && vital.diastolic <= 80) ? 'normal' :
          (vital.systolic > 130 || vital.diastolic > 90) ? 'abnormal' : 'borderline';
      case 'blood_sugar':
        const sv = parseFloat(vital.sugar_value || vital.value);
        if (vital.reading_type === 'fasting') return sv >= 70 && sv <= 100 ? 'normal' : (sv <= 125 ? 'borderline' : 'abnormal');
        if (vital.reading_type === 'pp') return sv < 140 ? 'normal' : (sv <= 199 ? 'borderline' : 'abnormal');
        if (vital.reading_type === 'hba1c') return sv < 5.7 ? 'normal' : (sv <= 6.4 ? 'borderline' : 'abnormal');
        return sv >= 70 && sv <= 100 ? 'normal' : (sv <= 125 ? 'borderline' : 'abnormal');
      case 'heart_rate':
        const hr = parseInt(vital.hr_value || vital.value);
        return (hr >= 60 && hr <= 100) ? 'normal' : 'abnormal';
      case 'spo2':
        const spo2 = parseFloat(vital.spo2_value || vital.value);
        return spo2 >= 95 ? 'normal' : 'abnormal';
      case 'temperature':
        const temp = parseFloat(vital.temp_value || vital.value);
        return (temp >= 36.1 && temp <= 37.2) ? 'normal' : 'abnormal';
      case 'weight':
        if (!vital.bmi) return 'normal';
        const bmi = parseFloat(vital.bmi);
        return (bmi >= 18.5 && bmi <= 24.9) ? 'normal' : (bmi < 29.9 ? 'borderline' : 'abnormal');
      default: return 'normal';
    }
  };

  // Format vital type for display
  const formatVitalType = (vital) => {
    if (!vital) return '-';
    if (vital.vital_type === 'blood_sugar' && vital.reading_type === 'hba1c') return 'HbA1c';
    if (vital.vital_type === 'spo2') return 'SpO2';
    return vital.vital_type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Combined format for charts and summary cards (value + unit)
  const formatVitalValue = (vital) => {
    if (!vital) return 'No data';
    const value = formatVitalValueOnly(vital);
    const unit = formatVitalUnit(vital);

    if (!value || value === '--') return 'No data';

    // Blood pressure already formatted as "120/80"
    if (vital.vital_type === 'blood_pressure') return `${value} ${unit}`;

    // Weight may include BMI info
    if (vital.vital_type === 'weight') return `${value} ${unit}`;

    // For blood sugar, the value may already include reading type label
    if (vital.vital_type === 'blood_sugar') {
      if (vital.reading_type === 'hba1c') return `${value}%`;
      return `${value} ${unit}`;
    }

    // For others, just concatenate
    return `${value} ${unit}`;
  };

  // Prepare chart data for a vital type. Blood sugar requires a readingType
  // (fasting/pp/random/hba1c) so mg/dL and % series never share one line.
  const getChartData = (type, readingType = null) => {
    const typeVitals = vitals
      .filter(v => {
        if (v.vital_type !== type) return false;
        if (type === 'blood_sugar' && readingType) {
          return (v.reading_type || 'random') === readingType;
        }
        return true;
      })
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))
      .slice(-30); // Last 30 entries, oldest first

    return typeVitals.map(v => {
      const date = new Date(v.recorded_at);
      const dataPoint = {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: v.recorded_at
      };

      if (type === 'blood_pressure') {
        dataPoint.systolic = v.systolic;
        dataPoint.diastolic = v.diastolic;
      } else if (type === 'weight') {
        dataPoint.weight = v.weight_value != null ? parseFloat(v.weight_value) : null;
        if (v.bmi) dataPoint.bmi = parseFloat(parseFloat(v.bmi).toFixed(1));
      } else if (type === 'blood_sugar') {
        dataPoint.value = parseFloat(v.sugar_value ?? v.value);
      } else if (type === 'heart_rate') {
        dataPoint.value = parseFloat(v.hr_value ?? v.value);
      } else if (type === 'spo2') {
        dataPoint.value = parseFloat(v.spo2_value ?? v.value);
      } else if (type === 'temperature') {
        dataPoint.value = parseFloat(v.temp_value ?? v.value);
      } else {
        dataPoint.value = parseFloat(v.value);
      }

      return dataPoint;
    });
  };

  // ============================================
  // TAB COMPONENTS & HELPER FUNCTIONS
  // ============================================

  // "08 Jun 2026, 10:27" style date
  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' +
      d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Compact Vital Summary Card Component
  const VitalSummaryCard = ({ title, value, unit, status, date, emoji }) => {
    const isEmpty = value === '--' || value === null || value === undefined;
    const topColor = status === 'normal' ? '#38a169' : status === 'abnormal' ? '#e53e3e' : status === 'warning' ? '#d97706' : '#e2e8f0';
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '14px 16px',
        boxShadow: isEmpty ? 'none' : '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        borderTop: isEmpty ? 'none' : `3px solid ${topColor}`,
        border: isEmpty ? '1.5px dashed #cbd5e0' : undefined,
        borderTopWidth: isEmpty ? '1.5px' : '3px',
        borderTopStyle: isEmpty ? 'dashed' : 'solid',
        borderTopColor: isEmpty ? '#cbd5e0' : topColor,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title.replace(/[^\x20-\x7E]+\s*/g, '').trim()}
        </span>
        {isEmpty ? (
          <div style={{ fontSize: '0.9rem', color: '#a0aec0', padding: '10px 0' }}>No data</div>
        ) : (
          <>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1a202c', fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace", lineHeight: 1.2 }}>
              {value}
            </div>
            {unit && <div style={{ fontSize: '0.75rem', color: '#718096' }}>{unit}</div>}
          </>
        )}
        {date && !isEmpty && (
          <div style={{ fontSize: '0.7rem', color: '#a0aec0', marginTop: 'auto' }}>
            {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        )}
      </div>
    );
  };

  // ── Helpers shared by both AI functions ──────────────────────────────────

  const _startPolling = useCallback(() => {
    if (pollTimerRef.current) return; // already running
    const poll = async () => {
      try {
        const res = await api.get(`${API_URL}/ai/history/${patientIdRef.current}`, { headers });
        const gens = res.data.generations || [];
        const stillPending = applyAiHistory(gens);
        if (!stillPending && pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      } catch (e) { console.error('AI poll error', e); }
    };
    pollTimerRef.current = setInterval(poll, 10000);
  }, [headers, applyAiHistory]);

  // Fetch AI health insights — fires the backend job and returns immediately;
  // the result is polled every 10 s and displayed when the DB row turns 'done'
  const fetchAIRecommendations = async () => {
    if (!patient || (vitals.length === 0 && labResults.length === 0)) {
      setAiRecommendation('Please add vitals or upload lab results first.');
      setAiGenerated(true);
      return;
    }

    // Group lab results by test name — keep trend (earliest → latest)
    const labSeries = {};
    labResults.forEach(r => {
      if (!isFinite(parseFloat(r.value))) return;
      if (!labSeries[r.test_name]) labSeries[r.test_name] = [];
      labSeries[r.test_name].push(r);
    });
    const abnormalLabs = [];
    Object.values(labSeries).forEach(series => {
      series.sort((a, b) => new Date(a.report_date) - new Date(b.report_date));
      const latest = series[series.length - 1];
      if (!latest.is_abnormal) return;
      abnormalLabs.push({
        name: latest.test_name,
        unit: latest.unit || '',
        range: latest.reference_range || '',
        latest: { value: latest.value, date: latest.report_date },
        earliest: series.length > 1 ? { value: series[0].value, date: series[0].report_date } : null
      });
    });

    // Group vitals by type+reading_type so HbA1c, fasting, PP are separate series
    const vitalSeriesMap = {};
    vitals.forEach(v => {
      const key = v.vital_type === 'blood_sugar'
        ? `blood_sugar_${v.reading_type || 'random'}`
        : v.vital_type;
      if (!vitalSeriesMap[key]) vitalSeriesMap[key] = [];
      vitalSeriesMap[key].push(v);
    });
    const abnormalVitalLines = [];
    Object.values(vitalSeriesMap).forEach(series => {
      series.sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
      const latest = series[series.length - 1];
      if (getVitalStatus(latest) === 'normal') return;
      const fmt = v => v.vital_type === 'blood_pressure'
        ? `${v.systolic}/${v.diastolic} mmHg`
        : `${formatVitalValueOnly(v)} ${formatVitalUnit(v)}`;
      const dt = v => new Date(v.recorded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      abnormalVitalLines.push(
        series.length > 1
          ? `${formatVitalType(latest)}: ${fmt(series[0])} (${dt(series[0])}) → ${fmt(latest)} (${dt(latest)})`
          : `${formatVitalType(latest)}: ${fmt(latest)} (${dt(latest)})`
      );
    });

    // All-normal: save a static message immediately, no AI call needed
    if (abnormalLabs.length === 0 && abnormalVitalLines.length === 0) {
      const allNormalMsg =
        `**Great news!** All your lab results and vitals are within normal range.\n\n` +
        `Keep up your healthy lifestyle! Here are some tips to stay that way:\n\n` +
        `🏃 Stay active with 30 minutes of exercise daily\n` +
        `💧 Drink 8 glasses of water throughout the day\n` +
        `🥗 Eat a balanced diet with plenty of vegetables\n` +
        `😴 Get 7-8 hours of quality sleep each night\n` +
        `🚫 Limit sugar, salt and processed foods\n` +
        `🩺 Schedule regular health checkups with your doctor`;
      try {
        await fetch(`${API_URL}/ai/record/${patientId}`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind: 'insights', content: allNormalMsg })
        });
      } catch (e) {}
      setAiRecommendation(allNormalMsg);
      setAiGenerated(true);
      setLastUpdated(new Date());
      return;
    }

    const age = patient.date_of_birth
      ? Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
      : '?';
    let prompt = `Patient profile: ${age} years old, ${patient.gender || 'gender not specified'}.\n\n`;

    if (abnormalLabs.length > 0) {
      prompt += `Out-of-range lab results (oldest → latest where multiple readings exist):\n`;
      abnormalLabs.slice(0, 20).forEach(l => {
        if (l.earliest && l.earliest.date !== l.latest.date)
          prompt += `- ${l.name}: ${l.earliest.value} (${l.earliest.date}) → ${l.latest.value} ${l.unit} (${l.latest.date}), normal: ${l.range}\n`;
        else
          prompt += `- ${l.name}: ${l.latest.value} ${l.unit} (${l.latest.date}), normal: ${l.range}\n`;
      });
      prompt += '\n';
    }

    if (abnormalVitalLines.length > 0) {
      prompt += `Out-of-range vitals (oldest → latest where multiple readings exist):\n`;
      abnormalVitalLines.forEach(line => { prompt += `- ${line}\n`; });
      prompt += '\n';
    }

    prompt +=
      `Analyse these out-of-range values. For each one:\n` +
      `1. Explain in plain language what the deviation means.\n` +
      `2. Suggest specific, practical lifestyle actions (food, exercise, sleep, habits).\n` +
      `3. If a value is borderline, say so.\n\n` +
      `Structure your response:\n` +
      `## Summary\n` +
      `[2-3 sentences]\n\n` +
      `## Key Findings and Actions\n` +
      `[One section per out-of-range result]\n\n` +
      `## Top Priority This Week\n` +
      `[Single most important action]\n\n` +
      `Use warm, plain language. Do NOT refuse to analyse — this is the user's own ` +
      `recorded data, not a clinical consultation.`;

    try {
      const res = await fetch(`${API_URL}/ai/insights`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, patient_id: patientId })
      });
      if (!res.ok) throw new Error(`${res.status}`);
      // 202: job queued — show pending state and start polling
      setAiPending(true);
      setAiGenerated(false);
      _startPolling();
    } catch (err) {
      console.error('AI insights start error:', err);
      setAiRecommendation('Could not start generation. Please try again.');
      setAiGenerated(true);
    }
  };

  // Build trend series and generate "Ask Your Doctor" questions (fire-and-forget)
  const fetchDoctorQuestions = async () => {
    if (!patient || (vitals.length === 0 && labResults.length === 0)) {
      setDoctorQuestions('Please add vitals or upload lab results first.');
      setDqGenerated(true);
      return;
    }

    try {
      // Lab trends: per test, oldest → latest values; only tests whose latest value is abnormal
      const labSeries = {};
      labResults.forEach(r => {
        if (!isFinite(parseFloat(r.value))) return;
        if (!labSeries[r.test_name]) labSeries[r.test_name] = [];
        labSeries[r.test_name].push(r);
      });
      const concerningLabs = [];
      Object.values(labSeries).forEach(series => {
        series.sort((a, b) => new Date(a.report_date) - new Date(b.report_date));
        const latest = series[series.length - 1];
        if (!latest.is_abnormal) return;
        const earliest = series[0];
        concerningLabs.push({
          name: latest.test_name,
          unit: latest.unit || '',
          range: latest.reference_range || '',
          latest: { value: latest.value, date: latest.report_date },
          earliest: series.length > 1 ? { value: earliest.value, date: earliest.report_date } : null
        });
      });
      // Cap prompt size: most recent abnormal results first
      concerningLabs.sort((a, b) => new Date(b.latest.date) - new Date(a.latest.date));
      const topLabs = concerningLabs.slice(0, 12);

      // Vital trends: earliest vs latest for key vitals
      const vitalTrends = [];
      const byType = {};
      vitals.forEach(v => {
        const key = v.vital_type === 'blood_sugar' ? `blood_sugar_${v.reading_type || 'random'}` : v.vital_type;
        if (!byType[key]) byType[key] = [];
        byType[key].push(v);
      });
      Object.entries(byType).forEach(([key, series]) => {
        series.sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
        const latest = series[series.length - 1];
        if (getVitalStatus(latest) === 'normal') return;
        const earliest = series[0];
        const fmt = (v) => v.vital_type === 'blood_pressure'
          ? `${v.systolic}/${v.diastolic} mmHg`
          : `${formatVitalValueOnly(v)} ${formatVitalUnit(v)}`;
        const dt = (v) => new Date(v.recorded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        vitalTrends.push(
          series.length > 1
            ? `${formatVitalType(latest)}: ${fmt(earliest)} (${dt(earliest)}) → ${fmt(latest)} (${dt(latest)})`
            : `${formatVitalType(latest)}: ${fmt(latest)} (${dt(latest)})`
        );
      });

      // Active medications (so questions account for current treatment)
      let activeMeds = [];
      try {
        const medRes = await fetch(`${API_URL}/medications/patient/${patientId}`, { headers });
        const medData = await medRes.json();
        activeMeds = (medData.medications || []).filter(m => m.is_active);
      } catch (e) {}

      const age = patient.date_of_birth
        ? Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
        : '?';

      let prompt = `Patient: ${age}${patient.gender ? patient.gender[0] : ''}\n\n`;

      if (topLabs.length > 0) {
        prompt += `Abnormal lab results (with trend where available):\n`;
        topLabs.forEach(l => {
          if (l.earliest && l.earliest.date !== l.latest.date) {
            prompt += `- ${l.name}: ${l.earliest.value} (${l.earliest.date}) → ${l.latest.value} ${l.unit} (${l.latest.date}), normal: ${l.range}\n`;
          } else {
            prompt += `- ${l.name}: ${l.latest.value} ${l.unit} (${l.latest.date}), normal: ${l.range}\n`;
          }
        });
        prompt += '\n';
      }

      if (vitalTrends.length > 0) {
        prompt += `Out-of-range vitals (oldest → latest):\n${vitalTrends.map(t => `- ${t}`).join('\n')}\n\n`;
      }

      if (activeMeds.length > 0) {
        prompt += `Current medications:\n${activeMeds.map(m =>
          `- ${m.medicine_name}${m.dosage ? ` ${m.dosage}` : ''}${m.frequency ? `, ${m.frequency}` : ''}`
        ).join('\n')}\n\n`;
      } else {
        prompt += `Current medications: none recorded.\n\n`;
      }

      prompt +=
        `Write 3-5 focused questions for the patient to ask at their next doctor visit.\n` +
        `Rules:\n` +
        `- Cover only the most clinically significant findings above.\n` +
        `- CRITICAL: copy every number VERBATIM from the data above. Never invent, estimate, ` +
        `round or alter any value. If you cannot find a value in the data, leave it out entirely.\n` +
        `- When a trend is available, use the form: "My [test] went from [first exact value] ` +
        `on [first date] to [latest exact value] on [latest date] — ..."\n` +
        `- If the patient is on a medication that targets a finding, ask about effectiveness ` +
        `or dose adjustment.\n` +
        `- Phrase each question in the patient's voice ("My ... was ... — should I ...?").\n` +
        `- Return ONLY a markdown bulleted list of the questions. No intro, no conclusion.`;

      const res = await fetch(`${API_URL}/ai/doctor-questions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, patient_id: patientId })
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setDqPending(true);
      setDqGenerated(false);
      _startPolling();
    } catch (err) {
      console.error('Doctor questions start error:', err.message);
      setDoctorQuestions('Could not start generation. Please try again.');
      setDqGenerated(true);
    }
  };

  // Overview Tab Component
  const OverviewTab = () => {
    return (
      <>
        {/* 9 Compact Vitals Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {/* Blood Pressure Card */}
          {(() => {
            const bp = getLatestVital('blood_pressure');
            const status = bp ? (bp.systolic <= 120 && bp.diastolic <= 80 ? 'normal' :
                        (bp.systolic > 130 || bp.diastolic > 80) ? 'abnormal' : 'warning') : 'none';
            return <VitalSummaryCard
              title="🫀 Blood Pressure"
              value={bp ? `${bp.systolic}/${bp.diastolic}` : '--'}
              unit="mmHg"
              status={status}
              date={bp?.recorded_at}
              emoji="🫀"
            />;
          })()}

          {/* Fasting Sugar Card */}
          {(() => {
            const latestFasting = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'fasting')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestFasting ? parseFloat(latestFasting.sugar_value || latestFasting.value) : null;
            const status = val === null ? 'none' : (val >= 70 && val <= 100 ? 'normal' : (val <= 125 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="Fasting Sugar"
              value={val !== null ? val.toFixed(0) : '--'}
              unit="mg/dL"
              status={status}
              date={latestFasting?.recorded_at}
              emoji="🩸"
            />;
          })()}

          {/* PP Sugar Card */}
          {(() => {
            const latestPP = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'pp')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestPP ? parseFloat(latestPP.sugar_value || latestPP.value) : null;
            const status = val === null ? 'none' : (val < 140 ? 'normal' : (val <= 199 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="PP Sugar"
              value={val !== null ? val.toFixed(0) : '--'}
              unit="mg/dL"
              status={status}
              date={latestPP?.recorded_at}
              emoji="🩸"
            />;
          })()}

          {/* Random Sugar Card */}
          {(() => {
            const latestRandom = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'random')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestRandom ? parseFloat(latestRandom.sugar_value || latestRandom.value) : null;
            const status = val === null ? 'none' : (val < 140 ? 'normal' : (val <= 199 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="Random Sugar"
              value={val !== null ? val.toFixed(0) : '--'}
              unit="mg/dL"
              status={status}
              date={latestRandom?.recorded_at}
              emoji="🩸"
            />;
          })()}

          {/* HbA1c Card */}
          {(() => {
            const latestHba1c = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'hba1c')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestHba1c ? parseFloat(latestHba1c.sugar_value || latestHba1c.value) : null;
            const status = val === null ? 'none' : (val < 5.7 ? 'normal' : (val <= 6.4 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="HbA1c"
              value={val !== null ? val.toFixed(1) : '--'}
              unit="%"
              status={status}
              date={latestHba1c?.recorded_at}
              emoji="💉"
            />;
          })()}

          {/* Heart Rate Card */}
          {(() => {
            const hr = getLatestVital('heart_rate');
            const val = hr ? parseInt(hr.hr_value || hr.value) : null;
            const status = val === null ? 'none' : (val >= 60 && val <= 100 ? 'normal' : 'abnormal');
            return <VitalSummaryCard
              title="Heart Rate"
              value={val !== null ? val : '--'}
              unit="bpm"
              status={status}
              date={hr?.recorded_at}
              emoji="❤️"
            />;
          })()}

          {/* SpO2 Card */}
          {(() => {
            const spo2 = getLatestVital('spo2');
            const val = spo2 ? parseFloat(spo2.spo2_value || spo2.value) : null;
            const status = val === null ? 'none' : (val >= 95 ? 'normal' : 'abnormal');
            return <VitalSummaryCard
              title="SpO2"
              value={val !== null ? val.toFixed(0) : '--'}
              unit="%"
              status={status}
              date={spo2?.recorded_at}
              emoji="🫁"
            />;
          })()}

          {/* Temperature Card */}
          {(() => {
            const temp = getLatestVital('temperature');
            const val = temp ? parseFloat(temp.temp_value || temp.value) : null;
            const tempC = val && temp.temp_unit === 'fahrenheit' ? (val - 32) * 5/9 : val;
            const status = val === null ? 'none' : (tempC >= 36.1 && tempC <= 37.2 ? 'normal' : 'abnormal');
            return <VitalSummaryCard
              title="Temperature"
              value={val !== null ? val.toFixed(1) : '--'}
              unit={temp?.temp_unit === 'fahrenheit' ? '°F' : '°C'}
              status={status}
              date={temp?.recorded_at}
              emoji="🌡️"
            />;
          })()}

          {/* Weight Card */}
          {(() => {
            const weight = getLatestVital('weight');
            const val = weight ? parseFloat(weight.weight_value || weight.value) : null;
            const bmi = weight?.bmi ? parseFloat(weight.bmi) : null;
            const status = !bmi ? 'none' : (bmi >= 18.5 && bmi <= 24.9 ? 'normal' : (bmi < 29.9 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="Weight"
              value={val !== null ? `${val.toFixed(1)}` : '--'}
              unit="kg"
              status={status}
              date={weight?.recorded_at}
              emoji="⚖️"
            />;
          })()}

          {/* BMI Card (shows separately) */}
          {(() => {
            const weight = getLatestVital('weight');
            const bmi = weight?.bmi ? parseFloat(weight.bmi) : null;
            const status = !bmi ? 'none' : (bmi >= 18.5 && bmi <= 24.9 ? 'normal' : (bmi < 29.9 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="BMI"
              value={bmi !== null ? bmi.toFixed(1) : '--'}
              unit=""
              status={status}
              date={null}
              emoji="📊"
            />;
          })()}
        </div>

        {/* Top 5 Abnormal Lab Results */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Top Abnormal Lab Results
          </h4>
          {labResults.filter(r => r.is_abnormal).length === 0 ? (
            <p style={{ color: '#718096' }}>No abnormal results 🎉</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Test</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Value</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Unit</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Ref Range</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labResults.filter(r => r.is_abnormal).slice(0, 5).map(lab => (
                      <tr key={lab.id} style={{ borderBottom: '1px solid #e2e8f0', background: '#fff5f5' }}>
                        <td style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>⚠️</span>
                          <span>{cleanTestName(lab.test_name, lab.category)}</span>
                        </td>
                        <td style={{ padding: '12px', color: '#e53e3e', fontWeight: '600' }}>{lab.value}</td>
                        <td style={{ padding: '12px', color: '#718096' }}>{lab.unit}</td>
                        <td style={{ padding: '12px', color: '#718096', fontSize: '0.85rem' }}>{lab.reference_range}</td>
                        <td style={{ padding: '12px' }}>{new Date(lab.report_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '12px', textAlign: 'right' }}>
                <button onClick={() => setActiveTab('lab_results')} style={{ border: 'none', background: 'none', color: '#2b6cb0', cursor: 'pointer', fontSize: '0.9rem' }}>
                  View All →
                </button>
              </div>
            </>
          )}
        </div>

        {/* AI Recommendations - Lazy Load */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🤖</span> AI Health Insights
            </h3>
            {aiGenerated && (
              <button
                onClick={() => {
                  setAiGenerated(false);
                  setAiRecommendation('');
                }}
                style={{
                  padding: '6px 16px',
                  backgroundColor: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                🔄 Regenerate
              </button>
            )}
          </div>
          {/* Pending banner — shown while AI job runs in background */}
          {aiPending && (
            <div style={{ padding: '16px 20px', background: '#ebf8ff', border: '1px solid #bee3f8',
              borderRadius: '10px', marginBottom: '14px', display: 'flex',
              alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '1.4rem' }}>🔄</div>
              <div>
                <div style={{ fontWeight: '600', color: '#2b6cb0' }}>AI analysis running in the background</div>
                <div style={{ fontSize: '0.85rem', color: '#4a5568', marginTop: '2px' }}>
                  Takes 2–5 minutes on this server. You can navigate away — the result will appear
                  automatically when ready. Checking every 10 s…
                </div>
              </div>
            </div>
          )}
          {!aiGenerated && !aiPending ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <p style={{ color: '#718096', marginBottom: '16px' }}>Analyzes your vitals and lab results to give personalised advice</p>
              <button
                onClick={fetchAIRecommendations}
                disabled={!patient || (vitals.length === 0 && labResults.length === 0)}
                style={{
                  padding: '12px 24px', backgroundColor: '#2b6cb0', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontSize: '1rem', fontWeight: '600'
                }}
              >
                🤖 Generate AI Health Insights
              </button>
            </div>
          ) : aiGenerated && !aiPending ? (
            <>
              <div style={{
                maxHeight: '400px', overflowY: 'auto', padding: '16px',
                background: '#f7fafc', borderRadius: '8px', marginTop: '12px',
                lineHeight: '1.8', color: '#2d3748'
              }}>
                <div dangerouslySetInnerHTML={{ __html: marked(aiRecommendation || '') }} />
              </div>
              {lastUpdated && (
                <div style={{ fontSize: '0.78rem', color: '#a0aec0', marginTop: '8px',
                  display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  {aiModel && (() => {
                    const { label, cloud } = formatModelLabel(aiModel);
                    return (
                      <span style={{ background: cloud ? '#ebf8ff' : '#f0fff4',
                        color: cloud ? '#2b6cb0' : '#276749',
                        padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
                        {cloud ? '☁️' : '🖥️'} {label}
                      </span>
                    );
                  })()}
                  <span>
                    Generated: {lastUpdated.toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric'
                    })} {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              <GenerationHistory
                history={aiHistory.filter(r => r.status === 'done')}
                show={showAiHistory}
                setShow={setShowAiHistory}
                currentShownAt={lastUpdated}
                onSelect={(rec) => {
                  setAiRecommendation(rec.content);
                  setLastUpdated(new Date(rec.created_at));
                }}
              />
            </>
          ) : null}
          {/* Ask Your Doctor */}
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <h4 style={{ margin: 0, color: '#2d3748', fontSize: '1rem' }}>Ask Your Doctor</h4>
              {dqGenerated && !dqPending && (
                <button
                  onClick={() => { setDqGenerated(false); setDoctorQuestions(''); }}
                  style={{ padding: '4px 12px', backgroundColor: '#e2e8f0', color: '#4a5568',
                    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500' }}
                >
                  Regenerate
                </button>
              )}
            </div>
            {dqPending && (
              <div style={{ padding: '12px 16px', background: '#ebf8ff', border: '1px solid #bee3f8',
                borderRadius: '8px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.1rem' }}>🔄</span>
                <span style={{ fontSize: '0.9rem', color: '#2b6cb0' }}>
                  Generating questions in background — you can navigate away and come back.
                </span>
              </div>
            )}
            {!dqGenerated && !dqPending ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ color: '#718096', marginBottom: '12px', fontSize: '0.9rem' }}>
                  Turn your results into focused questions for your next doctor visit
                </p>
                <button
                  onClick={fetchDoctorQuestions}
                  disabled={!patient || (vitals.length === 0 && labResults.length === 0)}
                  style={{ padding: '10px 20px', backgroundColor: '#38a169', color: 'white',
                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '0.9rem', fontWeight: '600' }}
                >
                  Generate Doctor Questions
                </button>
              </div>
            ) : dqGenerated && !dqPending ? (
              <>
                <p style={{ color: '#4a5568', fontSize: '0.9rem', marginBottom: '10px' }}>
                  Based on your recent results, here are a few questions worth raising at your next doctor visit:
                </p>
                <div style={{
                  padding: '14px 16px', background: '#f0fff4', border: '1px solid #c6f6d5',
                  borderRadius: '8px', lineHeight: '1.8', color: '#2d3748', fontSize: '0.92rem'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: marked(doctorQuestions || '') }} />
                </div>
                {dqUpdated && (
                  <div style={{ fontSize: '0.78rem', color: '#a0aec0', marginTop: '6px',
                    display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                    {dqModel && (() => {
                      const { label, cloud } = formatModelLabel(dqModel);
                      return (
                        <span style={{ background: cloud ? '#ebf8ff' : '#f0fff4',
                          color: cloud ? '#2b6cb0' : '#276749',
                          padding: '1px 8px', borderRadius: '10px', fontWeight: '500' }}>
                          {cloud ? '☁️' : '🖥️'} {label}
                        </span>
                      );
                    })()}
                    <span>
                      Generated: {dqUpdated.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} {dqUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                <GenerationHistory
                  history={dqHistory.filter(r => r.status === 'done')}
                  show={showDqHistory}
                  setShow={setShowDqHistory}
                  currentShownAt={dqUpdated}
                  onSelect={(rec) => {
                    setDoctorQuestions(rec.content);
                    setDqUpdated(new Date(rec.created_at));
                  }}
                />
              </>
            ) : null}
          </div>

          <div style={{ fontSize: '0.85rem', color: '#a0aec0', textAlign: 'right', marginTop: '8px' }}>
            ⚠️ AI can make mistakes. Always consult your doctor.
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
          <Link
            to={`/patients/${patientId}/vitals/new`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#38b2ac',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              textAlign: 'center'
            }}
          >
            ➕ Add Vital
          </Link>
          <Link
            to={`/patients/${patientId}/documents/new`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#2b6cb0',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              textAlign: 'center'
            }}
          >
            📤 Upload Doc
          </Link>
          <button
            disabled
            title="Coming soon"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#e2e8f0',
              color: '#718096',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'not-allowed'
            }}
          >
            💊 Medications
          </button>
          <button
            disabled
            title="Coming soon"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: '#e2e8f0',
              color: '#718096',
              border: 'none',
              borderRadius: '10px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'not-allowed'
            }}
          >
            📅 Appointments
          </button>
        </div>
      </>
    );
  };


  // Vitals Tab Component
  const VitalsTab = () => {
    const [vitalsPage, setVitalsPage] = useState(1);
    const VITALS_PER_PAGE = 20;

    // Compute date window from tagged documents for tag filter
    const tagDateWindow = useMemo(() => {
      if (!vitalTagFilter) return null;
      const taggedDocs = documents.filter(d =>
        d.tags && d.tags.some(t => t.id === vitalTagFilter.id)
      );
      if (taggedDocs.length === 0) return null;
      const dates = taggedDocs.map(d => new Date(d.created_at));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      minDate.setDate(minDate.getDate() - 3);
      maxDate.setDate(maxDate.getDate() + 3);
      return { min: minDate, max: maxDate };
    }, [documents, vitalTagFilter]);

    const filteredVitals = useMemo(() => {
      let result = vitals.filter(v => {
        const matchType = !vitalSearch ||
          v.vital_type === vitalSearch ||
          (vitalSearch === 'hba1c' && v.vital_type === 'blood_sugar' && v.reading_type === 'hba1c');
        const vitalFrom = vitalDateRange[0];
        const vitalTo = vitalDateRange[1];
        const vitalDate = dayjs(v.recorded_at);
        const matchFrom = !vitalFrom || vitalDate.isSameOrAfter(vitalFrom, 'day');
        const matchTo = !vitalTo || vitalDate.isSameOrBefore(vitalTo, 'day');
        const matchTagDate = !tagDateWindow || (() => {
          const vDate = new Date(v.recorded_at);
          return vDate >= tagDateWindow.min && vDate <= tagDateWindow.max;
        })();
        return matchType && matchFrom && matchTo && matchTagDate;
      });

      result = result.sort((a, b) => {
        let aVal = a[vitalSortField] || '';
        let bVal = b[vitalSortField] || '';
        return vitalSortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });

      return result;
    }, [vitals, vitalSearch, vitalDateRange, vitalSortField, vitalSortDir, tagDateWindow]);

    useEffect(() => { setVitalsPage(1); }, [filteredVitals.length]);

    const handleVitalSort = (field) => {
      if (vitalSortField === field) setVitalSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setVitalSortField(field); setVitalSortDir('asc'); }
    };

    const exportVitalsCSV = () => {
      const headers = ['Date & Time','Type','Reading','Unit','Notes'];
      const rows = filteredVitals.map(v => [
        escapeCSV(new Date(v.recorded_at).toLocaleString()),
        escapeCSV(formatVitalType(v)),
        escapeCSV(formatVitalValueOnly(v)),
        escapeCSV(formatVitalUnit(v)),
        escapeCSV(v.notes || '')
      ]);
      const csv = [headers.map(escapeCSV).join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vitals_${patient.first_name}.csv`;
      a.click();
    };

    const totalVitalsPages = Math.ceil(filteredVitals.length / VITALS_PER_PAGE);
    const pagedVitals = filteredVitals.slice((vitalsPage - 1) * VITALS_PER_PAGE, vitalsPage * VITALS_PER_PAGE);

    return (
      <div>
        {/* Filter Bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={vitalSearch}
            onChange={e => setVitalSearch(e.target.value)}
            style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }}
          >
            <option value="">All Types</option>
            <option value="blood_pressure">Blood Pressure</option>
            <option value="blood_sugar">Blood Sugar</option>
            <option value="hba1c">HbA1c</option>
            <option value="weight">Weight</option>
            <option value="heart_rate">Heart Rate</option>
            <option value="spo2">SpO2</option>
            <option value="temperature">Temperature</option>
          </select>
          <RangePicker
            value={vitalDateRange}
            onChange={(dates) => setVitalDateRange(dates || [null, null])}
            format="DD-MMM-YYYY"
            placeholder={['From date', 'To date']}
            allowClear
            size="middle"
            popupStyle={{ zIndex: 1050 }}
            style={{ borderRadius: '8px', width: '280px' }}
            presets={[
              { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), dayjs()] },
              { label: 'Last 3 months', value: [dayjs().subtract(3, 'month'), dayjs()] },
              { label: 'Last 6 months', value: [dayjs().subtract(6, 'month'), dayjs()] },
              { label: 'Last 1 year', value: [dayjs().subtract(1, 'year'), dayjs()] },
              { label: 'This year', value: [dayjs().startOf('year'), dayjs()] },
            ]}
          />
          <button
            onClick={() => { setVitalSearch(''); setVitalDateRange([null, null]); }}
            style={{ padding: '10px 20px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
          >
            Clear
          </button>
          {/* Tag filter */}
          {allPatientTags.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
              <span style={{ fontSize: '0.85rem', color: '#718096' }}>Tag:</span>
              <button
                onClick={() => setVitalTagFilter(null)}
                style={{
                  padding: '4px 12px', borderRadius: '12px', border: '1px solid #e2e8f0',
                  background: !vitalTagFilter ? '#2b6cb0' : 'white',
                  color: !vitalTagFilter ? 'white' : '#4a5568',
                  cursor: 'pointer', fontSize: '0.8rem'
                }}>
                All
              </button>
              {allPatientTags.map(tag => (
                <button key={tag.id}
                  onClick={() => setVitalTagFilter(
                    vitalTagFilter?.id === tag.id ? null : tag
                  )}
                  style={{
                    padding: '4px 12px', borderRadius: '12px',
                    border: `1px solid ${tag.color}`,
                    background: vitalTagFilter?.id === tag.id ? tag.color : tag.color + '15',
                    color: vitalTagFilter?.id === tag.id ? 'white' : tag.color,
                    cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
                  }}>
                  {tag.name}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={exportVitalsCSV}
            style={{ padding: '10px 20px', backgroundColor: '#38a169', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}
          >
            📥 Export CSV
          </button>
          <Link
            to={`/patients/${patientId}/vitals/new`}
            style={{ marginLeft: 'auto', padding: '10px 20px', backgroundColor: '#2b6cb0', color: 'white', textDecoration: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600' }}
          >
            + Quick Add Vital
          </Link>
        </div>

        {/* 9 Compact Vitals Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {/* Reuse the 9 cards (same as OverviewTab) */}
          {(() => {
            const bp = getLatestVital('blood_pressure');
            const bpStatus = bp ? (bp.systolic <= 120 && bp.diastolic <= 80 ? 'normal' : (bp.systolic > 130 || bp.diastolic > 80 ? 'abnormal' : 'warning')) : 'none';
            return <VitalSummaryCard title="🫀 Blood Pressure" value={bp ? `${bp.systolic}/${bp.diastolic}` : '--'} unit="mmHg" status={bpStatus} date={bp?.recorded_at} emoji="🫀" />;
          })()}
          {(() => {
            const latestFasting = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'fasting')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestFasting ? parseFloat(latestFasting.sugar_value || latestFasting.value) : null;
            const status = val === null ? 'none' : (val >= 70 && val <= 100 ? 'normal' : (val <= 125 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard title="Fasting Sugar" value={val !== null ? val.toFixed(0) : '--'} unit="mg/dL" status={status} date={latestFasting?.recorded_at} emoji="🩸" />;
          })()}
          {(() => {
            const latestPP = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'pp')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestPP ? parseFloat(latestPP.sugar_value || latestPP.value) : null;
            const status = val === null ? 'none' : (val < 140 ? 'normal' : (val <= 199 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard title="PP Sugar" value={val !== null ? val.toFixed(0) : '--'} unit="mg/dL" status={status} date={latestPP?.recorded_at} emoji="🩸" />;
          })()}

          {/* Random Sugar Card */}
          {(() => {
            const latestRandom = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'random')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestRandom ? parseFloat(latestRandom.sugar_value || latestRandom.value) : null;
            const status = val === null ? 'none' : (val < 140 ? 'normal' : (val <= 199 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard
              title="Random Sugar"
              value={val !== null ? val.toFixed(0) : '--'}
              unit="mg/dL"
              status={status}
              date={latestRandom?.recorded_at}
              emoji="🩸"
            />;
          })()}

          {(() => {
            const latestHba1c = [...vitals]
              .filter(v => v.vital_type === 'blood_sugar' && v.reading_type === 'hba1c')
              .sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at))[0];
            const val = latestHba1c ? parseFloat(latestHba1c.sugar_value || latestHba1c.value) : null;
            const status = val === null ? 'none' : (val < 5.7 ? 'normal' : (val <= 6.4 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard title="HbA1c" value={val !== null ? val.toFixed(1) : '--'} unit="%" status={status} date={latestHba1c?.recorded_at} emoji="💉" />;
          })()}
          {(() => {
            const hr = getLatestVital('heart_rate');
            const val = hr ? parseInt(hr.hr_value || hr.value) : null;
            const status = val === null ? 'none' : (val >= 60 && val <= 100 ? 'normal' : 'abnormal');
            return <VitalSummaryCard title="Heart Rate" value={val !== null ? val : '--'} unit="bpm" status={status} date={hr?.recorded_at} emoji="❤️" />;
          })()}
          {(() => {
            const spo2 = getLatestVital('spo2');
            const val = spo2 ? parseFloat(spo2.spo2_value || spo2.value) : null;
            const status = val === null ? 'none' : (val >= 95 ? 'normal' : 'abnormal');
            return <VitalSummaryCard title="SpO2" value={val !== null ? val.toFixed(0) : '--'} unit="%" status={status} date={spo2?.recorded_at} emoji="🫁" />;
          })()}
          {(() => {
            const temp = getLatestVital('temperature');
            const val = temp ? parseFloat(temp.temp_value || temp.value) : null;
            const tempC = val && temp.temp_unit === 'fahrenheit' ? (val - 32) * 5/9 : val;
            const status = val === null ? 'none' : (tempC >= 36.1 && tempC <= 37.2 ? 'normal' : 'abnormal');
            return <VitalSummaryCard title="Temperature" value={val !== null ? val.toFixed(1) : '--'} unit={temp?.temp_unit === 'fahrenheit' ? '°F' : '°C'} status={status} date={temp?.recorded_at} emoji="🌡️" />;
          })()}
          {(() => {
            const weight = getLatestVital('weight');
            const val = weight ? parseFloat(weight.weight_value || weight.value) : null;
            const bmi = weight?.bmi ? parseFloat(weight.bmi) : null;
            const status = !bmi ? 'none' : (bmi >= 18.5 && bmi <= 24.9 ? 'normal' : (bmi < 29.9 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard title="Weight" value={val !== null ? `${val.toFixed(1)}` : '--'} unit="kg" status={status} date={weight?.recorded_at} emoji="⚖️" />;
          })()}
          {(() => {
            const weight = getLatestVital('weight');
            const bmi = weight?.bmi ? parseFloat(weight.bmi) : null;
            const status = !bmi ? 'none' : (bmi >= 18.5 && bmi <= 24.9 ? 'normal' : (bmi < 29.9 ? 'warning' : 'abnormal'));
            return <VitalSummaryCard title="BMI" value={bmi !== null ? bmi.toFixed(1) : '--'} unit="" status={status} date={null} emoji="📊" />;
          })()}
        </div>

        {/* Vitals History Table */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '16px' }}>Vital History</h4>
          {pagedVitals.length === 0 ? (
            <p style={{ color: '#718096', textAlign: 'center', padding: '40px' }}>No vital records match your filters.</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                      {[
                        { key: 'recorded_at', label: 'Date & Time' },
                        { key: 'vital_type', label: 'Type' },
                        { key: null, label: 'Reading' },
                        { key: null, label: 'Unit' },
                        { key: null, label: 'Status' },
                        { key: null, label: 'Notes' },
                        { key: null, label: 'Actions' }
                      ].map(col => (
                        <th key={col.label} style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: col.key ? 'pointer' : 'default',
                          color: '#4a5568',
                          fontWeight: '600'
                        }} onClick={() => col.key ? handleVitalSort(col.key) : null}>
                          {col.label}
                          {col.key && vitalSortField === col.key && (vitalSortDir === 'asc' ? <span style={{marginLeft: '4px'}}>↑</span> : <span style={{marginLeft: '4px'}}>↓</span>)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedVitals.map(vital => (
                      <tr key={vital.id} style={{ borderBottom: '1px solid #e2e8f0', transition: 'background 0.15s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px' }}>{formatDateTime(vital.recorded_at)}</td>
                        <td style={{ padding: '12px' }}>{formatVitalType(vital)}</td>
                        <td style={{ padding: '12px' }}>{formatVitalValueOnly(vital)}</td>
                        <td style={{ padding: '12px', fontSize: '0.85rem', color: '#718096' }}>{formatVitalUnit(vital)}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <StatusBadge status={getVitalStatus(vital)} />
                        </td>
                        <td style={{ padding: '12px', color: '#718096' }}>{vital.notes || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteVital(vital.id)}
                            style={{
                              padding: '4px 10px',
                              backgroundColor: 'transparent',
                              color: '#a0aec0',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              transition: 'all 0.15s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#c53030';
                              e.currentTarget.style.borderColor = '#fed7d7';
                              e.currentTarget.style.backgroundColor = '#fff5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#a0aec0';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalVitalsPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', alignItems: 'center' }}>
                  <button
                    onClick={() => setVitalsPage(p => Math.max(1, p - 1))}
                    disabled={vitalsPage === 1}
                    style={{ padding: '8px 16px', backgroundColor: vitalsPage === 1 ? '#e2e8f0' : '#2b6cb0', color: vitalsPage === 1 ? '#718096' : 'white', border: 'none', borderRadius: '6px', cursor: vitalsPage === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Previous
                  </button>
                  <span style={{ color: '#4a5568', fontSize: '0.9rem' }}>
                    Page {vitalsPage} of {totalVitalsPages} ({filteredVitals.length} total)
                  </span>
                  <button
                    onClick={() => setVitalsPage(p => Math.min(totalVitalsPages, p + 1))}
                    disabled={vitalsPage === totalVitalsPages}
                    style={{ padding: '8px 16px', backgroundColor: vitalsPage === totalVitalsPages ? '#e2e8f0' : '#2b6cb0', color: vitalsPage === totalVitalsPages ? '#718096' : 'white', border: 'none', borderRadius: '6px', cursor: vitalsPage === totalVitalsPages ? 'not-allowed' : 'pointer' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Trend Charts — one per vital parameter (matches the summary cards above) */}
        <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}>
          {[
            { title: '🫀 Blood Pressure Trend', data: getChartData('blood_pressure'),
              domain: ['dataMin - 10', 'dataMax + 10'],
              lines: [
                { dataKey: 'systolic', stroke: '#f56565', name: 'Systolic (mmHg)' },
                { dataKey: 'diastolic', stroke: '#4299e1', name: 'Diastolic (mmHg)' }
              ] },
            { title: '🩸 Fasting Sugar Trend', data: getChartData('blood_sugar', 'fasting'),
              domain: ['dataMin - 10', 'dataMax + 20'],
              lines: [{ dataKey: 'value', stroke: '#9f7aea', name: 'Fasting Sugar (mg/dL)' }] },
            { title: '🩸 PP Sugar Trend', data: getChartData('blood_sugar', 'pp'),
              domain: ['dataMin - 10', 'dataMax + 20'],
              lines: [{ dataKey: 'value', stroke: '#b83280', name: 'PP Sugar (mg/dL)' }] },
            { title: '🩸 Random Sugar Trend', data: getChartData('blood_sugar', 'random'),
              domain: ['dataMin - 10', 'dataMax + 20'],
              lines: [{ dataKey: 'value', stroke: '#6b46c1', name: 'Random Sugar (mg/dL)' }] },
            { title: '🧪 HbA1c Trend', data: getChartData('blood_sugar', 'hba1c'),
              domain: ['dataMin - 0.5', 'dataMax + 0.5'],
              lines: [{ dataKey: 'value', stroke: '#d53f8c', name: 'HbA1c (%)' }] },
            { title: '❤️ Heart Rate Trend', data: getChartData('heart_rate'),
              domain: ['dataMin - 10', 'dataMax + 10'],
              lines: [{ dataKey: 'value', stroke: '#ed8936', name: 'Heart Rate (bpm)' }] },
            { title: '🫁 SpO2 Trend', data: getChartData('spo2'),
              domain: [80, 100],
              lines: [{ dataKey: 'value', stroke: '#48bb78', name: 'SpO2 %' }] },
            { title: '🌡️ Temperature Trend', data: getChartData('temperature'),
              domain: ['dataMin - 1', 'dataMax + 1'],
              lines: [{ dataKey: 'value', stroke: '#f687b3', name: 'Temperature (°C/°F)' }] },
            { title: '⚖️ Weight Trend', data: getChartData('weight'),
              domain: ['dataMin - 5', 'dataMax + 5'],
              lines: [{ dataKey: 'weight', stroke: '#38b2ac', name: 'Weight (kg)' }] },
            { title: '📊 BMI Trend', data: getChartData('weight').filter(d => d.bmi != null),
              domain: ['dataMin - 2', 'dataMax + 2'],
              lines: [{ dataKey: 'bmi', stroke: '#3182ce', name: 'BMI' }] }
          ].map(chart => (
            <div key={chart.title} style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <h4 style={{ marginBottom: '16px' }}>{chart.title}</h4>
              {chart.data.length === 0 ? (
                <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#a0aec0', fontSize: '0.9rem', background: '#f7fafc', borderRadius: '8px' }}>
                  No readings yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chart.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#718096" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#718096" domain={chart.domain} />
                    <Tooltip />
                    <Legend />
                    {chart.lines.map(line => (
                      <Line key={line.dataKey} type="monotone" dataKey={line.dataKey}
                        stroke={line.stroke} strokeWidth={2} dot={{ r: 4 }} name={line.name} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const EncyclopediaTab = React.memo(({ headers, patientId }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [allTests, setAllTests] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [aiLoading, setAiLoading] = React.useState(null);
  const [aiResults, setAiResults] = React.useState({});
  const [expandedTest, setExpandedTest] = React.useState(null);
  const [selectedCondition, setSelectedCondition] = React.useState({});

  const renderMarkdown = (text) => {
    if (!text) return null;
    const html = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^## (.+)$/gm, '<h4 style="color:#2d3748;margin:12px 0 6px">$1</h4>')
      .replace(/^### (.+)$/gm, '<h5 style="color:#4a5568;margin:10px 0 4px">$1</h5>')
      .replace(/^\* (.+)$/gm, '<li style="margin:3px 0">$1</li>')
      .replace(/^- (.+)$/gm, '<li style="margin:3px 0">$1</li>')
      .replace(/(<li.*<\/li>)/gs, '<ul style="padding-left:20px;margin:6px 0">$1</ul>')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
    return <div dangerouslySetInnerHTML={{ __html: html }}
      style={{ color: '#2d3748', fontSize: '0.9rem', lineHeight: '1.7' }} />;
  };

  React.useEffect(() => {
    const fetchAllTests = async () => {
      try {
        const res = await fetch(
          `${API_URL}/interpretations/encyclopedia/all-tests`,
          { headers }
        );
        const data = await res.json();
        setAllTests(data.tests || []);
      } catch {}
    };
    fetchAllTests();
  }, [headers]);

  const handleSearch = React.useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/interpretations/encyclopedia/search?q=${encodeURIComponent(searchQuery)}`,
        { headers }
      );
      const data = await res.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  React.useEffect(() => {
    const timer = setTimeout(() => handleSearch(query), 400);
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  const fetchAIExplanation = async (testName, condition = 'general') => {
    const key = `${testName}_${condition}`;
    if (aiResults[key]) return;

    const INVALID_PATTERNS = [
      /^\d+$/,
      /^[^a-zA-Z]+$/,
    ];
    const MIN_LENGTH = 2;
    const MAX_LENGTH = 100;

    if (testName.length < MIN_LENGTH || testName.length > MAX_LENGTH) {
      setAiResults(prev => ({ ...prev, [key]: 'Please enter a valid test name.' }));
      return;
    }
    if (INVALID_PATTERNS.some(p => p.test(testName.trim()))) {
      setAiResults(prev => ({ ...prev, [key]: 'Please enter a valid medical test name.' }));
      return;
    }

    setAiLoading(key);
    try {
      const res = await fetch(
        `${API_URL}/interpretations/encyclopedia/ai-explain`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ test_name: testName, condition })
        }
      );
      const data = await res.json();
      setAiResults(prev => ({ ...prev, [key]: data.explanation }));
    } catch {
      setAiResults(prev => ({ ...prev, [key]: 'AI explanation unavailable.' }));
    } finally {
      setAiLoading(null);
    }
  };

  const CONDITION_COLORS = {
    high: { bg: '#fff5f5', border: '#fed7d7', badge: '#e53e3e', label: '▲ High' },
    low: { bg: '#ebf8ff', border: '#bee3f8', badge: '#2b6cb0', label: '▼ Low' },
    positive: { bg: '#fffaf0', border: '#fbd38d', badge: '#c05621', label: '+ Positive' },
    normal: { bg: '#f0fff4', border: '#9ae6b4', badge: '#276749', label: '✓ Normal' },
  };

  const popularTests = ['HbA1c', 'Vitamin D, 25-OH', 'Vitamin B12',
    'TSH', 'Hemoglobin (Hb)', 'High Sensitivity C-Reactive Protein (hsCRP)',
    'Triglycerides', 'HDL Cholesterol', 'Ferritin', 'Creatinine'];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 6px', color: '#2d3748' }}>
          📚 Health Encyclopedia
        </h3>
        <p style={{ margin: 0, color: '#718096', fontSize: '0.9rem' }}>
          Search any medical test to understand what it means and what to do
        </p>
      </div>

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: '20px' }}>
        <span style={{ position: 'absolute', left: '16px', top: '50%',
          transform: 'translateY(-50%)', color: '#a0aec0', pointerEvents: 'none', display: 'flex' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </span>
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search test name... e.g. HbA1c, CRP, Vitamin D, Creatinine"
          style={{
            width: '100%',
            height: '48px',
            padding: '0 44px',
            fontSize: '1rem',
            border: '2px solid #e2e8f0',
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={e => e.target.style.borderColor = '#2b6cb0'}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
        {loading && (
          <div style={{ position: 'absolute', right: '16px', top: '50%',
            transform: 'translateY(-50%)', color: '#a0aec0', fontSize: '0.85rem' }}>
            Searching...
          </div>
        )}
        {query && !loading && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            style={{ position: 'absolute', right: '16px', top: '50%',
              transform: 'translateY(-50%)', background: 'none',
              border: 'none', cursor: 'pointer', color: '#a0aec0',
              fontSize: '1.2rem' }}>
            ✕
          </button>
        )}
      </div>

      {/* Popular tests */}
      {!query && results.length === 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '0.85rem', color: '#718096',
            marginBottom: '10px', fontWeight: '500' }}>
            🔥 Commonly searched tests:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {popularTests.map(test => (
              <button key={test}
                onClick={() => setQuery(test)}
                style={{
                  padding: '6px 14px',
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: '#4a5568',
                }}
                onMouseEnter={e => {
                  e.target.style.background = '#2b6cb0';
                  e.target.style.color = 'white';
                  e.target.style.borderColor = '#2b6cb0';
                }}
                onMouseLeave={e => {
                  e.target.style.background = '#f7fafc';
                  e.target.style.color = '#4a5568';
                  e.target.style.borderColor = '#e2e8f0';
                }}>
                {test}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No results — offer AI explanation */}
      {query.length >= 2 && !loading && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px',
          background: '#f7fafc', borderRadius: '12px',
          border: '1px dashed #e2e8f0' }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🤔</div>
          <p style={{ color: '#4a5568', marginBottom: '16px', fontSize: '0.95rem' }}>
            <strong>"{query}"</strong> is not in our knowledge base yet.
          </p>
          {aiLoading === `${query}_general` && (
            <div style={{ color: '#a0aec0', fontSize: '0.95rem', fontWeight: '600' }}>
              ⏳ Getting AI explanation...
            </div>
          )}
          {!aiResults[`${query}_general`] && aiLoading !== `${query}_general` && (
            <button
              onClick={() => fetchAIExplanation(query, 'general')}
              style={{
                padding: '10px 24px',
                background: '#2b6cb0',
                color: 'white',
                border: 'none', borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600', fontSize: '0.95rem'
              }}>
              🤖 Ask AI to explain this test
            </button>
          )}
          {aiResults[`${query}_general`] && (
            <div style={{ marginTop: '20px', textAlign: 'left',
              background: 'white', borderRadius: '10px',
              padding: '20px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '12px' }}>
                <span style={{ fontSize: '0.75rem', background: '#ebf8ff',
                  color: '#2b6cb0', padding: '2px 8px', borderRadius: '8px',
                  fontWeight: '500' }}>🤖 AI Generated</span>
              </div>
              {renderMarkdown(aiResults[`${query}_general`])}
            </div>
          )}
        </div>
      )}

      {/* Search results */}
      {results.length > 0 && (
        <div>
          <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '14px' }}>
            Found {results.length} test{results.length !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {results.map(result => (
              <div key={result.test_name}
                style={{ background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '12px', overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'box-shadow 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}>

                {/* Test header */}
                <div
                  onClick={() => setExpandedTest(
                    expandedTest === result.test_name ? null : result.test_name
                  )}
                  style={{ padding: '16px 20px', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', background: '#f7fafc',
                    borderBottom: expandedTest === result.test_name
                      ? '1px solid #e2e8f0' : 'none' }}>
                  <div>
                    <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '1rem' }}>
                      {result.test_name}
                    </div>
                    <div style={{ marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {result.conditions.map(c => {
                        const colors = CONDITION_COLORS[c.condition] || CONDITION_COLORS.normal;
                        return (
                          <span key={c.condition}
                            style={{ fontSize: '0.72rem',
                              background: colors.bg,
                              color: colors.badge,
                              border: `1px solid ${colors.border}`,
                              padding: '1px 8px', borderRadius: '10px',
                              fontWeight: '500' }}>
                            {colors.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <span style={{ color: '#a0aec0', fontSize: '1.2rem' }}>
                    {expandedTest === result.test_name ? '▲' : '▼'}
                  </span>
                </div>

                {/* Expanded conditions */}
                {expandedTest === result.test_name && (
                  <div style={{ padding: '16px 20px' }}>

                    {/* Condition selector if multiple */}
                    {result.conditions.length > 1 && (
                      <div style={{ display: 'flex', gap: '8px',
                        marginBottom: '16px', flexWrap: 'wrap' }}>
                        {result.conditions.map(c => {
                          const colors = CONDITION_COLORS[c.condition] || CONDITION_COLORS.normal;
                          const isSelected = (selectedCondition[result.test_name] ||
                            result.conditions[0].condition) === c.condition;
                          return (
                            <button key={c.condition}
                              onClick={() => setSelectedCondition(prev => ({
                                ...prev, [result.test_name]: c.condition
                              }))}
                              style={{
                                padding: '5px 14px',
                                borderRadius: '20px',
                                border: `1px solid ${colors.border}`,
                                background: isSelected ? colors.badge : colors.bg,
                                color: isSelected ? 'white' : colors.badge,
                                cursor: 'pointer',
                                fontSize: '0.82rem',
                                fontWeight: '500'
                              }}>
                              {colors.label}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Show selected condition's interpretation */}
                    {result.conditions
                      .filter(c => c.condition === (selectedCondition[result.test_name] ||
                        result.conditions[0].condition))
                      .slice(0, 1)
                      .map(c => (
                        <div key={c.condition}>
                          {c.interpretation && (
                            <div style={{ marginBottom: '14px', padding: '12px',
                              background: '#fffbf0', borderRadius: '8px',
                              borderLeft: '3px solid #d69e2e' }}>
                              <div style={{ fontWeight: '600', color: '#744210',
                                fontSize: '0.82rem', marginBottom: '4px' }}>
                                📋 What this means
                              </div>
                              {renderMarkdown(c.interpretation)}
                            </div>
                          )}

                          <div style={{ display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '10px', marginBottom: '10px' }}>
                            {c.dietary_advice && (
                              <div style={{ padding: '10px 14px',
                                background: '#f0fff4', borderRadius: '8px',
                                border: '1px solid #9ae6b4' }}>
                                <div style={{ fontWeight: '600', color: '#276749',
                                  fontSize: '0.82rem', marginBottom: '4px' }}>
                                  🥗 Diet
                                </div>
                                <div style={{ color: '#2d3748', fontSize: '0.85rem', lineHeight: '1.5' }}>
                                  {c.dietary_advice}
                                </div>
                              </div>
                            )}
                            {c.lifestyle_advice && (
                              <div style={{ padding: '10px 14px',
                                background: '#ebf8ff', borderRadius: '8px',
                                border: '1px solid #bee3f8' }}>
                                <div style={{ fontWeight: '600', color: '#2b6cb0',
                                  fontSize: '0.82rem', marginBottom: '4px' }}>
                                  🏃 Lifestyle
                                </div>
                                <div style={{ color: '#2d3748', fontSize: '0.85rem', lineHeight: '1.5' }}>
                                  {c.lifestyle_advice}
                                </div>
                              </div>
                            )}
                          </div>

                          {c.followup_tests && (
                            <div style={{ padding: '10px 14px',
                              background: '#faf5ff', borderRadius: '8px',
                              border: '1px solid #d6bcfa', marginBottom: '10px' }}>
                              <div style={{ fontWeight: '600', color: '#553c9a',
                                fontSize: '0.82rem', marginBottom: '4px' }}>
                                🧪 Follow-up tests
                              </div>
                              <div style={{ color: '#2d3748', fontSize: '0.85rem' }}>
                                {c.followup_tests}
                              </div>
                            </div>
                          )}

                          {c.see_doctor_if && (
                            <div style={{ padding: '10px 14px',
                              background: '#fff5f5', borderRadius: '8px',
                              border: '1px solid #fed7d7' }}>
                              <div style={{ fontWeight: '600', color: '#c53030',
                                fontSize: '0.82rem', marginBottom: '4px' }}>
                                🏥 See doctor if
                              </div>
                              <div style={{ color: '#2d3748', fontSize: '0.85rem' }}>
                                {c.see_doctor_if}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  });

  const MedicationsTab = React.memo(({ patient, patientId, headers }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const [medications, setMedications] = React.useState([]);
  const [todayMeds, setTodayMeds] = React.useState([]);
  const [logs, setLogs] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [selectedMed, setSelectedMed] = React.useState(null);
  const [showCalendar, setShowCalendar] = React.useState(null);
  const [calendarLogs, setCalendarLogs] = React.useState([]);
  const [showRefill, setShowRefill] = React.useState(null);
  const [refillQty, setRefillQty] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState('active');
  const [showInteractionChecker, setShowInteractionChecker] = React.useState(false);
  const [selectedForCheck, setSelectedForCheck] = React.useState([]);
  const [interactionResult, setInteractionResult] = React.useState(null);
  const [interactionLoading, setInteractionLoading] = React.useState(false);
  const [groups, setGroups] = React.useState([]);
  const [editingGroupNotes, setEditingGroupNotes] = React.useState(null); // group id
  const [groupNotesDraft, setGroupNotesDraft] = React.useState('');
  const emptyForm = {
    medicine_name: '', dosage: '', frequency: '',
    times_per_day: 1, instructions: '', start_date: '',
    end_date: '', quantity_on_hand: '', daily_quantity: '',
    unit: 'tablets', group_id: '', new_group_name: ''
  };
  const [form, setForm] = React.useState(emptyForm);

  const fetchMedications = React.useCallback(async () => {
    try {
      setLoading(true);
      const [medsRes, todayRes, groupsRes] = await Promise.all([
        fetch(`${API_URL}/medications/patient/${patientId}`, { headers }),
        fetch(`${API_URL}/medications/patient/${patientId}/today`, { headers }),
        fetch(`${API_URL}/medications/patient/${patientId}/groups`, { headers })
      ]);
      const medsData = await medsRes.json();
      const todayData = await todayRes.json();
      const groupsData = await groupsRes.json();
      setMedications(medsData.medications || []);
      setTodayMeds(todayData.medications || []);
      setGroups(groupsData.groups || []);
    } catch(e) {
      console.error('Error fetching medications:', e);
    } finally {
      setLoading(false);
    }
  }, [patientId, headers]);

  React.useEffect(() => { fetchMedications(); }, [fetchMedications]);

  const handleLog = async (medId, status) => {
    try {
      await fetch(`${API_URL}/medications/${medId}/log`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          log_date: new Date().toISOString().split('T')[0]
        })
      });
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  const handleRefill = async (medId) => {
    if (!refillQty || isNaN(refillQty)) return;
    try {
      await fetch(`${API_URL}/medications/${medId}/refill`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity_added: parseFloat(refillQty) })
      });
      setShowRefill(null);
      setRefillQty('');
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  const handleDelete = async (medId) => {
    if (!window.confirm('Delete this medication?')) return;
    try {
      await fetch(`${API_URL}/medications/${medId}`, {
        method: 'DELETE', headers
      });
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  const handleSubmit = async () => {
    if (!form.medicine_name.trim()) return;
    try {
      // "New group" option: create the incident group first, then attach
      let groupId = form.group_id === '__new__' ? null : (form.group_id || null);
      if (form.group_id === '__new__' && form.new_group_name.trim()) {
        const gRes = await fetch(`${API_URL}/medications/patient/${patientId}/groups`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: form.new_group_name.trim() })
        });
        if (gRes.ok) groupId = (await gRes.json()).group.id;
      }
      const url = selectedMed
        ? `${API_URL}/medications/${selectedMed.id}`
        : `${API_URL}/medications/patient/${patientId}`;
      const method = selectedMed ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          group_id: groupId,
          times_per_day: parseInt(form.times_per_day) || 1,
          quantity_on_hand: form.quantity_on_hand ? parseFloat(form.quantity_on_hand) : null,
          daily_quantity: form.daily_quantity ? parseFloat(form.daily_quantity) : null,
        })
      });
      setShowAddForm(false);
      setSelectedMed(null);
      setForm(emptyForm);
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  // Mark a single medication's course as finished
  const handleMarkComplete = async (medId, complete = true) => {
    try {
      await fetch(`${API_URL}/medications/${medId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !complete })
      });
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  // Mark a whole incident group (and all its medications) as complete
  const handleGroupStatus = async (group, status) => {
    if (status === 'completed' &&
        !window.confirm(`Mark "${group.name}" and all its medications as complete?`)) return;
    try {
      await fetch(`${API_URL}/medications/groups/${group.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  const handleGroupNotesSave = async (groupId) => {
    try {
      await fetch(`${API_URL}/medications/groups/${groupId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: groupNotesDraft })
      });
      setEditingGroupNotes(null);
      setGroupNotesDraft('');
      fetchMedications();
    } catch(e) { console.error(e); }
  };

  const fetchCalendarLogs = async (medId) => {
    try {
      const res = await fetch(
        `${API_URL}/medications/${medId}/logs?days=60`,
        { headers }
      );
      const data = await res.json();
      setCalendarLogs(data.logs || []);
    } catch(e) { console.error(e); }
  };

  const checkInteractions = async () => {
    if (selectedForCheck.length < 2) return;
    setInteractionLoading(true);
    setInteractionResult(null);
    try {
      const res = await fetch(`${API_URL}/medications/interactions/check`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ medicines: selectedForCheck })
      });
      const data = await res.json();
      setInteractionResult(data);
    } catch(e) {
      setInteractionResult({ error: 'Check failed. Please try again.' });
    } finally {
      setInteractionLoading(false);
    }
  };

  const filteredMeds = medications.filter(m => {
    if (activeFilter === 'active') return m.is_active;
    if (activeFilter === 'completed') return !m.is_active;
    return true;
  });

  const getDaysRemainingBadge = (med) => {
    const days = med.days_remaining;
    if (days === null) return null;
    if (days <= 3) return { color: '#e53e3e', bg: '#fff5f5', text: `⚠️ ${days}d left` };
    if (days <= 7) return { color: '#dd6b20', bg: '#fffaf0', text: `🔶 ${days}d left` };
    return { color: '#38a169', bg: '#f0fff4', text: `✅ ${days}d left` };
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
      Loading medications...
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ margin: 0, color: '#2d3748' }}>💊 Medications</h3>
          <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.85rem' }}>
            {medications.filter(m => m.is_active).length} active medications
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { setShowAddForm(true); setSelectedMed(null); setForm(emptyForm); }}
            style={{ padding: '8px 16px', background: '#2b6cb0', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontWeight: '600', fontSize: '0.9rem' }}>
            + Add Medication
          </button>
        </div>
      </div>

      {/* Drug Interaction Checker panel */}
      {showInteractionChecker && (
        <div style={{ background: '#faf5ff', border: '1px solid #d6bcfa',
          borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 8px', color: '#553c9a', fontSize: '1rem' }}>
            ⚗️ Drug Interaction Checker
          </h4>
          <p style={{ margin: '0 0 16px', color: '#718096', fontSize: '0.85rem' }}>
            Select 2 or more medications to check for interactions
          </p>

          {/* Medicine selection from active meds */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            {medications.filter(m => m.is_active).map(med => {
              const isSelected = selectedForCheck.includes(med.medicine_name);
              return (
                <button key={med.id}
                  onClick={() => {
                    setSelectedForCheck(prev =>
                      isSelected
                        ? prev.filter(n => n !== med.medicine_name)
                        : [...prev, med.medicine_name]
                    );
                    setInteractionResult(null);
                  }}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    border: `2px solid ${isSelected ? '#805ad5' : '#e2e8f0'}`,
                    background: isSelected ? '#805ad5' : 'white',
                    color: isSelected ? 'white' : '#4a5568',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: isSelected ? '600' : '400',
                    transition: 'all 0.15s'
                  }}>
                  {isSelected ? '✓ ' : ''}{med.medicine_name}
                  {med.dosage ? ` ${med.dosage}` : ''}
                </button>
              );
            })}
          </div>

          {/* Type custom medicine name */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Or type a medicine name and press Enter..."
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d6bcfa',
                borderRadius: '8px', fontSize: '0.85rem' }}
              onKeyPress={e => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const name = e.target.value.trim();
                  if (!selectedForCheck.includes(name)) {
                    setSelectedForCheck(prev => [...prev, name]);
                    setInteractionResult(null);
                  }
                  e.target.value = '';
                }
              }}
            />
          </div>

          {/* Selected medicines tags */}
          {selectedForCheck.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '0.82rem', color: '#718096', marginBottom: '6px' }}>
                Checking interactions between:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {selectedForCheck.map(name => (
                  <span key={name} style={{ background: '#805ad5', color: 'white',
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.82rem',
                    display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {name}
                    <span
                      onClick={() => setSelectedForCheck(prev => prev.filter(n => n !== name))}
                      style={{ cursor: 'pointer', fontWeight: '700', opacity: 0.8 }}>
                      ×
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Check button */}
          <button
            onClick={checkInteractions}
            disabled={selectedForCheck.length < 2 || interactionLoading}
            style={{
              padding: '10px 24px',
              background: selectedForCheck.length < 2 ? '#e2e8f0' :
                interactionLoading ? '#b794f4' : '#805ad5',
              color: selectedForCheck.length < 2 ? '#a0aec0' : 'white',
              border: 'none', borderRadius: '8px',
              cursor: selectedForCheck.length < 2 ? 'not-allowed' : 'pointer',
              fontWeight: '600', fontSize: '0.95rem'
            }}>
            {interactionLoading
              ? '⏳ Checking interactions...'
              : selectedForCheck.length < 2
                ? 'Select at least 2 medicines'
                : `⚗️ Check ${selectedForCheck.length} Medicines`}
          </button>

          {/* Results */}
          {interactionResult && (
            <div style={{ marginTop: '20px', background: 'white',
              borderRadius: '10px', padding: '20px',
              border: '1px solid #d6bcfa' }}>
              {interactionResult.error ? (
                <div style={{ color: '#c53030' }}>
                  ❌ {interactionResult.error}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ fontWeight: '600', color: '#553c9a' }}>
                      ⚗️ Interaction Analysis
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#a0aec0' }}>
                      🤖 AI powered • {new Date(interactionResult.checked_at)
                        .toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div style={{ color: '#2d3748', fontSize: '0.9rem',
                    lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                    {interactionResult.interactions}
                  </div>
                  <div style={{ marginTop: '14px', padding: '10px 14px',
                    background: '#fffbf0', borderRadius: '8px',
                    border: '1px solid #fbd38d', fontSize: '0.8rem',
                    color: '#744210' }}>
                    ⚠️ This is AI-generated information for reference only.
                    Always consult your doctor or pharmacist before making
                    changes to your medications.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Today's medications checklist */}
      {todayMeds.length > 0 && (
        <div style={{ background: 'white', border: '1px solid #e2e8f0',
          borderRadius: '12px', overflow: 'hidden', marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)' }}>
          <h4 style={{ margin: 0, padding: '12px 16px', color: 'white', fontSize: '1rem',
            background: 'linear-gradient(135deg, #2b6cb0 0%, #3730a3 100%)' }}>
            Today's Medications — {new Date().toLocaleDateString('en-IN',
              { weekday: 'long', day: 'numeric', month: 'short' })}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px' }}>
            {todayMeds.map(med => (
              <div key={med.id} style={{ display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', background: 'white',
                borderRadius: '8px', padding: '10px 14px',
                border: `2px solid ${
                  med.today_status === 'taken' ? '#9ae6b4' :
                  med.today_status === 'skipped' ? '#fbd38d' : '#e2e8f0'
                }` }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#2d3748' }}>
                    {med.medicine_name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                    {med.dosage} {med.dosage && '•'} {med.frequency}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {med.today_status === 'taken' ? (
                    <span style={{ background: '#f0fff4', color: '#22543d', fontWeight: '600',
                      fontSize: '0.8rem', padding: '4px 12px', borderRadius: '12px' }}>
                      Taken
                    </span>
                  ) : med.today_status === 'skipped' ? (
                    <span style={{ color: '#d69e2e', fontWeight: '600', fontSize: '0.9rem' }}>
                      ⏭️ Skipped
                    </span>
                  ) : (
                    <>
                      <button onClick={() => handleLog(med.id, 'taken')}
                        style={{ padding: '5px 12px', background: '#38a169',
                          color: 'white', border: 'none', borderRadius: '6px',
                          cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                        ✓ Taken
                      </button>
                      <button onClick={() => handleLog(med.id, 'skipped')}
                        style={{ padding: '5px 12px', background: '#e2e8f0',
                          color: '#4a5568', border: 'none', borderRadius: '6px',
                          cursor: 'pointer', fontSize: '0.85rem' }}>
                        Skip
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['active', 'completed', 'all'].map(f => (
          <button key={f} onClick={() => setActiveFilter(f)}
            style={{ padding: '6px 16px', borderRadius: '20px',
              border: '1px solid #e2e8f0', cursor: 'pointer',
              background: activeFilter === f ? '#2b6cb0' : 'white',
              color: activeFilter === f ? 'white' : '#4a5568',
              fontWeight: activeFilter === f ? '600' : '400',
              fontSize: '0.85rem', textTransform: 'capitalize' }}>
            {f}
          </button>
        ))}
      </div>

      {/* Medications list */}
      {filteredMeds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>💊</div>
          <p>No medications found. Add one manually or upload a prescription.</p>
        </div>
      ) : (() => {
        const renderMedCard = (med) => {
            const badge = getDaysRemainingBadge(med);
            const medStatusColor = !med.is_active ? '#e53e3e' :
              (med.days_remaining !== null && med.days_remaining <= 7) ? '#d97706' : '#38a169';
            return (
              <div key={med.id} style={{ background: 'white',
                border: '1px solid #e2e8f0', borderRadius: '12px',
                borderLeft: `4px solid ${medStatusColor}`,
                padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center',
                      gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '1rem' }}>
                        {med.medicine_name}
                      </span>
                      {med.source === 'imported' && (
                        <span style={{ fontSize: '0.7rem', background: '#edf2f7',
                          color: '#718096', padding: '1px 8px', borderRadius: '8px' }}>
                          imported
                        </span>
                      )}
                      {!med.is_active && (
                        <span style={{ fontSize: '0.7rem', background: '#e2e8f0',
                          color: '#718096', padding: '1px 6px', borderRadius: '8px' }}>
                          completed
                        </span>
                      )}
                      {badge && (
                        <span style={{ fontSize: '0.75rem', background: badge.bg,
                          color: badge.color, padding: '2px 8px', borderRadius: '8px',
                          fontWeight: '600' }}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: '6px', fontSize: '0.85rem',
                      color: '#718096', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {med.dosage && <span>💊 {med.dosage}</span>}
                      {med.frequency && <span>🔁 {med.frequency}</span>}
                      {med.start_date && (
                        <span>📅 {new Date(med.start_date).toLocaleDateString('en-IN')}
                          {med.end_date ? ` → ${new Date(med.end_date).toLocaleDateString('en-IN')}` : ''}
                        </span>
                      )}
                      {med.quantity_on_hand !== null && (
                        <span>📦 {med.quantity_on_hand} {med.unit} remaining</span>
                      )}
                    </div>
                    {med.instructions && (
                      <div style={{ marginTop: '4px', fontSize: '0.8rem',
                        color: '#a0aec0', fontStyle: 'italic' }}>
                        {med.instructions}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => {
                      setShowCalendar(showCalendar === med.id ? null : med.id);
                      fetchCalendarLogs(med.id);
                    }}
                      title="View adherence history"
                      style={{ padding: '5px 10px', background: '#ebf8ff',
                        color: '#2b6cb0', border: '1px solid #bee3f8',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                      📅 History
                    </button>
                    {med.quantity_on_hand !== null && (
                      <button onClick={() => setShowRefill(med.id)}
                        title="Log refill"
                        style={{ padding: '5px 10px', background: '#f0fff4',
                          color: '#276749', border: '1px solid #9ae6b4',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        ➕ Refill
                      </button>
                    )}
                    <button onClick={() => {
                      setSelectedMed(med);
                      setForm({
                        medicine_name: med.medicine_name || '',
                        dosage: med.dosage || '',
                        frequency: med.frequency || '',
                        times_per_day: med.times_per_day || 1,
                        instructions: med.instructions || '',
                        start_date: med.start_date || '',
                        end_date: med.end_date || '',
                        quantity_on_hand: med.quantity_on_hand || '',
                        daily_quantity: med.daily_quantity || '',
                        unit: med.unit || 'tablets',
                        group_id: med.group_id || '',
                        new_group_name: ''
                      });
                      setShowAddForm(true);
                    }}
                      style={{ padding: '5px 10px', background: '#fefcbf',
                        color: '#744210', border: '1px solid #faf089',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                      ✏️ Edit
                    </button>
                    {med.is_active ? (
                      <button onClick={() => handleMarkComplete(med.id, true)}
                        title="Mark this course as finished"
                        style={{ padding: '5px 10px', background: '#f0fff4',
                          color: '#276749', border: '1px solid #9ae6b4',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem',
                          fontWeight: '600' }}>
                        ✓ Complete
                      </button>
                    ) : (
                      <button onClick={() => handleMarkComplete(med.id, false)}
                        title="Resume this medication"
                        style={{ padding: '5px 10px', background: '#ebf8ff',
                          color: '#2b6cb0', border: '1px solid #bee3f8',
                          borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                        ↩ Reactivate
                      </button>
                    )}
                    <button onClick={() => handleDelete(med.id)}
                      style={{ padding: '5px 10px', background: '#fff5f5',
                        color: '#c53030', border: '1px solid #feb2b2',
                        borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Refill modal inline */}
                {showRefill === med.id && (
                  <div style={{ marginTop: '12px', padding: '12px',
                    background: '#f0fff4', borderRadius: '8px',
                    border: '1px solid #9ae6b4', display: 'flex',
                    alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.9rem', color: '#276749', fontWeight: '500' }}>
                      Add refill quantity:
                    </span>
                    <input type="number" value={refillQty}
                      onChange={e => setRefillQty(e.target.value)}
                      placeholder="e.g. 30"
                      style={{ width: '80px', padding: '5px 8px',
                        border: '1px solid #9ae6b4', borderRadius: '6px',
                        fontSize: '0.9rem' }} />
                    <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                      {med.unit}
                    </span>
                    <button onClick={() => handleRefill(med.id)}
                      style={{ padding: '5px 14px', background: '#38a169',
                        color: 'white', border: 'none', borderRadius: '6px',
                        cursor: 'pointer', fontSize: '0.85rem' }}>
                      Save
                    </button>
                    <button onClick={() => setShowRefill(null)}
                      style={{ padding: '5px 10px', background: '#e2e8f0',
                        color: '#4a5568', border: 'none', borderRadius: '6px',
                        cursor: 'pointer', fontSize: '0.85rem' }}>
                      Cancel
                    </button>
                  </div>
                )}

                {/* Calendar history */}
                {showCalendar === med.id && (
                  <div style={{ marginTop: '12px', padding: '12px',
                    background: '#f7fafc', borderRadius: '8px',
                    border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: '600', color: '#4a5568',
                      marginBottom: '10px', fontSize: '0.9rem' }}>
                      Adherence — last 60 days
                    </div>
                    {calendarLogs.length === 0 ? (
                      <p style={{ color: '#a0aec0', fontSize: '0.85rem', margin: 0 }}>
                        No logs yet. Mark doses as taken to see history.
                      </p>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px',
                          marginBottom: '10px' }}>
                          {calendarLogs.map(log => (
                            <div key={log.id}
                              title={`${log.log_date}: ${log.status}`}
                              style={{
                                width: '28px', height: '28px',
                                borderRadius: '4px', fontSize: '0.65rem',
                                display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontWeight: '600',
                                background: log.status === 'taken' ? '#9ae6b4' :
                                  log.status === 'skipped' ? '#fbd38d' : '#fed7d7',
                                color: log.status === 'taken' ? '#276749' :
                                  log.status === 'skipped' ? '#744210' : '#c53030',
                                cursor: 'default'
                              }}>
                              {new Date(log.log_date).getDate()}
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#718096',
                          display: 'flex', gap: '12px' }}>
                          <span>✅ Taken: {calendarLogs.filter(l => l.status === 'taken').length}</span>
                          <span>⏭️ Skipped: {calendarLogs.filter(l => l.status === 'skipped').length}</span>
                          <span>❌ Missed: {calendarLogs.filter(l => l.status === 'missed').length}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
        };

        // Group medications by incident; meds without a group go last
        const sections = groups
          .map(g => ({ group: g, meds: filteredMeds.filter(m => m.group_id === g.id) }))
          .filter(s => s.meds.length > 0);
        const groupIds = new Set(groups.map(g => g.id));
        const ungrouped = filteredMeds.filter(m => !m.group_id || !groupIds.has(m.group_id));

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {sections.map(({ group, meds }) => (
              <div key={group.id} style={{ border: '1px solid #e2e8f0', borderRadius: '14px',
                background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                {/* Group header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: '10px', flexWrap: 'wrap', padding: '12px 16px', background: '#f7fafc',
                  borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', color: '#2d3748' }}>🗂️ {group.name}</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: '600', padding: '2px 10px',
                      borderRadius: '10px',
                      background: group.status === 'completed' ? '#e2e8f0' : '#f0fff4',
                      color: group.status === 'completed' ? '#718096' : '#22543d' }}>
                      {group.status === 'completed' ? 'Completed' : 'Active'}
                    </span>
                    <span style={{ fontSize: '0.78rem', color: '#a0aec0' }}>
                      {meds.length} medicine{meds.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {group.status !== 'completed' ? (
                      <button onClick={() => handleGroupStatus(group, 'completed')}
                        style={{ padding: '5px 12px', background: '#f0fff4', color: '#276749',
                          border: '1px solid #9ae6b4', borderRadius: '6px', cursor: 'pointer',
                          fontSize: '0.8rem', fontWeight: '600' }}>
                        ✓ Mark Group Complete
                      </button>
                    ) : (
                      <button onClick={() => handleGroupStatus(group, 'active')}
                        style={{ padding: '5px 12px', background: '#ebf8ff', color: '#2b6cb0',
                          border: '1px solid #bee3f8', borderRadius: '6px', cursor: 'pointer',
                          fontSize: '0.8rem' }}>
                        ↩ Reopen
                      </button>
                    )}
                    <button onClick={() => {
                      setEditingGroupNotes(editingGroupNotes === group.id ? null : group.id);
                      setGroupNotesDraft(group.notes || '');
                    }}
                      style={{ padding: '5px 12px', background: '#fffbeb', color: '#92400e',
                        border: '1px solid #fbd38d', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.8rem' }}>
                      📝 {group.notes ? 'Edit Notes' : 'Add Notes'}
                    </button>
                  </div>
                </div>

                {/* Group notes */}
                {editingGroupNotes === group.id ? (
                  <div style={{ padding: '10px 16px', background: '#fffbeb',
                    borderBottom: '1px solid #e2e8f0' }}>
                    <textarea value={groupNotesDraft}
                      onChange={e => setGroupNotesDraft(e.target.value)}
                      rows={3}
                      placeholder="Remarks for this illness/incident, e.g. diagnosis, doctor advice, follow-up date..."
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #fbd38d',
                        borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box',
                        resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button onClick={() => handleGroupNotesSave(group.id)}
                        style={{ padding: '5px 14px', background: '#38a169', color: 'white',
                          border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                        Save Notes
                      </button>
                      <button onClick={() => { setEditingGroupNotes(null); setGroupNotesDraft(''); }}
                        style={{ padding: '5px 12px', background: '#e2e8f0', color: '#4a5568',
                          border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : group.notes ? (
                  <div style={{ padding: '10px 16px', background: '#fffbeb',
                    borderBottom: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#744210',
                    whiteSpace: 'pre-wrap' }}>
                    📝 {group.notes}
                  </div>
                ) : null}

                <div style={{ padding: '14px 16px', display: 'flex',
                  flexDirection: 'column', gap: '12px' }}>
                  {meds.map(renderMedCard)}
                </div>
              </div>
            ))}

            {ungrouped.length > 0 && (
              <div>
                {sections.length > 0 && (
                  <div style={{ fontWeight: '600', color: '#4a5568', margin: '4px 0 10px',
                    fontSize: '0.9rem' }}>
                    Other medications
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {ungrouped.map(renderMedCard)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Add/Edit form modal */}
      {showAddForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 2000, display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px',
            padding: '24px', width: '100%', maxWidth: '520px',
            maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: '#2d3748' }}>
              {selectedMed ? '✏️ Edit Medication' : '+ Add Medication'}
            </h3>

            {[
              { label: 'Medicine Name *', field: 'medicine_name', type: 'text', placeholder: 'e.g. Metformin 500mg' },
              { label: 'Dosage', field: 'dosage', type: 'text', placeholder: 'e.g. 500mg' },
              { label: 'Frequency', field: 'frequency', type: 'text', placeholder: 'e.g. Twice daily after food' },
              { label: 'Instructions', field: 'instructions', type: 'text', placeholder: 'e.g. Take with meals' },
              { label: 'Start Date', field: 'start_date', type: 'date' },
              { label: 'End Date', field: 'end_date', type: 'date' },
            ].map(({ label, field, type, placeholder }) => (
              <div key={field} style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', marginBottom: '4px',
                  fontSize: '0.85rem', fontWeight: '500', color: '#4a5568' }}>
                  {label}
                </label>
                <input type={type} value={form[field]}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '8px 10px',
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    fontSize: '0.9rem', boxSizing: 'border-box' }} />
              </div>
            ))}

            {/* Incident / illness grouping */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '4px',
                fontSize: '0.85rem', fontWeight: '500', color: '#4a5568' }}>
                Group (illness / incident)
              </label>
              <select value={form.group_id}
                onChange={e => setForm(f => ({ ...f, group_id: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px',
                  border: '1px solid #e2e8f0', borderRadius: '6px',
                  fontSize: '0.9rem', boxSizing: 'border-box' }}>
                <option value="">No group</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}{g.status === 'completed' ? ' (completed)' : ''}
                  </option>
                ))}
                <option value="__new__">＋ Create new group…</option>
              </select>
              {form.group_id === '__new__' && (
                <input type="text" value={form.new_group_name}
                  onChange={e => setForm(f => ({ ...f, new_group_name: e.target.value }))}
                  placeholder='e.g. "Viral Fever - June 2026" or "Diabetes (ongoing)"'
                  style={{ width: '100%', padding: '8px 10px', marginTop: '8px',
                    border: '1px solid #2b6cb0', borderRadius: '6px',
                    fontSize: '0.9rem', boxSizing: 'border-box' }} />
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: '10px', marginBottom: '14px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px',
                  fontSize: '0.85rem', fontWeight: '500', color: '#4a5568' }}>
                  Quantity on Hand
                </label>
                <input type="number" value={form.quantity_on_hand}
                  onChange={e => setForm(f => ({ ...f, quantity_on_hand: e.target.value }))}
                  placeholder="e.g. 30"
                  style={{ width: '100%', padding: '8px 10px',
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    fontSize: '0.9rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px',
                  fontSize: '0.85rem', fontWeight: '500', color: '#4a5568' }}>
                  Daily Quantity
                </label>
                <input type="number" value={form.daily_quantity}
                  onChange={e => setForm(f => ({ ...f, daily_quantity: e.target.value }))}
                  placeholder="e.g. 2"
                  style={{ width: '100%', padding: '8px 10px',
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    fontSize: '0.9rem', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px',
                  fontSize: '0.85rem', fontWeight: '500', color: '#4a5568' }}>
                  Unit
                </label>
                <select value={form.unit}
                  onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px',
                    border: '1px solid #e2e8f0', borderRadius: '6px',
                    fontSize: '0.9rem', boxSizing: 'border-box' }}>
                  <option>tablets</option>
                  <option>capsules</option>
                  <option>ml</option>
                  <option>drops</option>
                  <option>sachets</option>
                  <option>injections</option>
                  <option>patches</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={handleSubmit}
                style={{ flex: 1, padding: '10px', background: '#2b6cb0',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem' }}>
                {selectedMed ? 'Update' : 'Add Medication'}
              </button>
              <button onClick={() => { setShowAddForm(false); setSelectedMed(null); }}
                style={{ flex: 1, padding: '10px', background: '#e2e8f0',
                  color: '#4a5568', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '0.95rem' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

  // Appointments Tab Placeholder
  const AppointmentsTab = () => (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#718096' }}>
      <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📅</div>
      <h3>Appointments Coming Soon</h3>
      <p>Schedule and track doctor appointments and set reminders.</p>
    </div>
  );

  // Documents Tab Component
  const DocumentsTab = () => {
    const filteredDocs = useMemo(() => {
      return documents.filter(d => {
        const matchType = docFilter === 'all' || d.document_type === docFilter;
        const matchTag = !selectedFilterTag || (d.tags && d.tags.some(t => t.id === selectedFilterTag.id));
        return matchType && matchTag;
      });
    }, [documents, docFilter, selectedFilterTag]);

    return (
      <div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={docFilter}
            onChange={e => setDocFilter(e.target.value)}
            style={{ padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }}
          >
            <option value="all">All Documents</option>
            <option value="prescription">Prescriptions</option>
            <option value="lab_report">Lab Reports</option>
            <option value="discharge_summary">Discharge Summaries</option>
            <option value="xray_scan">X-Ray/Scans</option>
            <option value="insurance">Insurance</option>
            <option value="vaccination">Vaccinations</option>
            <option value="other">Other</option>
          </select>
          <Link
            to={`/patients/${patientId}/documents/new`}
            style={{ marginLeft: 'auto', padding: '10px 20px', backgroundColor: '#2b6cb0', color: 'white', textDecoration: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600' }}
          >
            📤 Upload Document
          </Link>
        </div>

        {/* Tag filter */}
        {allPatientTags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', color: '#718096' }}>Filter by tag:</span>
            <button
              onClick={() => setSelectedFilterTag(null)}
              style={{
                padding: '4px 12px', borderRadius: '12px', border: '1px solid #e2e8f0',
                background: !selectedFilterTag ? '#2b6cb0' : 'white',
                color: !selectedFilterTag ? 'white' : '#4a5568',
                cursor: 'pointer', fontSize: '0.8rem'
              }}>
              All
            </button>
            {allPatientTags.map(tag => (
              <button key={tag.id}
                onClick={() => setSelectedFilterTag(
                  selectedFilterTag?.id === tag.id ? null : tag
                )}
                style={{
                  padding: '4px 12px', borderRadius: '12px',
                  border: `1px solid ${tag.color}`,
                  background: selectedFilterTag?.id === tag.id ? tag.color : tag.color + '15',
                  color: selectedFilterTag?.id === tag.id ? 'white' : tag.color,
                  cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500'
                }}>
                {tag.name}
              </button>
            ))}
          </div>
        )}

        <DocumentList
          patientId={patientId}
          documents={filteredDocs}
          onRefresh={fetchDocuments}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Navbar user={user} onLogout={handleLogout} />
        <div className="dashboard-content">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <Navbar user={user} onLogout={handleLogout} />
        <div className="dashboard-content">
          <div className="api-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="dashboard">
        <Navbar user={user} onLogout={handleLogout} />
        <div className="dashboard-content">
          <div className="api-error">Patient not found</div>
        </div>
      </div>
    );
  }

  const age = calculateAge(patient.date_of_birth);
  const isOwnPatient = patient.user_id === user.id;

  return (
    <div className="dashboard">
      <Navbar user={user} />

      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {/* ── Sidebar ── */}
        <aside style={{
          width: sidebarCollapsed ? '64px' : '240px',
          flexShrink: 0,
          background: 'white',
          borderRight: '1px solid #e2e8f0',
          transition: 'width 0.2s ease',
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: 'calc(100vh - 56px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Collapse toggle + back link */}
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            padding: '12px', borderBottom: '1px solid #e2e8f0'
          }}>
            {!sidebarCollapsed && (
              <Link to="/dashboard" style={{ color: '#2b6cb0', textDecoration: 'none', fontSize: '0.85rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                ← All Patients
              </Link>
            )}
            <button
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: '1px solid #e2e8f0', borderRadius: '8px',
                cursor: 'pointer', color: '#718096', flexShrink: 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {sidebarCollapsed
                  ? <><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></>
                  : <><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></>}
              </svg>
            </button>
          </div>

          {/* FAMILY section */}
          <div style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0' }}>
            {!sidebarCollapsed && (
              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#a0aec0', letterSpacing: '0.1em', padding: '0 8px', marginBottom: '8px' }}>
                FAMILY
              </div>
            )}
            {familyPatients.map(member => {
              const isSelected = member.patient_id === patientId;
              const rel = (member.relationship_to_admin || '').toLowerCase();
              const circleColor = rel === 'self' ? '#2b6cb0' :
                (rel === 'spouse' || rel === 'child') ? '#38a169' :
                rel === 'parent' ? '#7c3aed' : '#718096';
              const memberAge = member.date_of_birth
                ? Math.floor((new Date() - new Date(member.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
                : null;
              return (
                <button
                  key={member.patient_id}
                  onClick={() => { if (!isSelected) { setActiveTab('overview'); navigate(`/patients/${member.patient_id}`); } }}
                  title={`${member.first_name} ${member.last_name}`}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    gap: '10px', padding: sidebarCollapsed ? '6px 0' : '8px 10px',
                    marginBottom: '4px', border: 'none', borderRadius: '8px',
                    cursor: isSelected ? 'default' : 'pointer', textAlign: 'left',
                    background: isSelected ? '#ebf8ff' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f7fafc'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isSelected ? '#ebf8ff' : 'transparent'; }}
                >
                  <span style={{
                    width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                    background: circleColor, color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: '700',
                    border: isSelected ? '2px solid #2b6cb0' : '2px solid transparent'
                  }}>
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </span>
                  {!sidebarCollapsed && (
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '0.88rem', fontWeight: isSelected ? '700' : '500', color: '#2d3748', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {member.first_name} {member.last_name?.[0]}.
                      </span>
                      <span style={{ display: 'block', fontSize: '0.7rem', color: '#a0aec0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {member.relationship_to_admin || 'patient'}{memberAge !== null ? `, ${memberAge}` : ''}
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <nav style={{ padding: '12px 8px', flex: 1 }}>
            {[
              { id: 'overview', label: 'Overview', icon: <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></> },
              { id: 'health_summary', label: 'Abnormal Results', icon: <><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></> },
              { id: 'lab_results', label: 'Lab Reports', icon: <><path d="M9 2v6L4 18a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3l-5-10V2"/><path d="M7 2h10"/></> },
              { id: 'vitals', label: 'Vitals', icon: <><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></> },
              { id: 'medications', label: 'Medications', icon: <><path d="M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7z"/><path d="m8.5 8.5 7 7"/></> },
              { id: 'encyclopedia', label: 'Encyclopedia', icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></> },
              { id: 'appointments', label: 'Appointments', icon: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></> },
              { id: 'documents', label: 'Documents', icon: <><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M13 2v7h7"/></> }
            ].map(item => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  title={item.label}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    gap: '10px', padding: sidebarCollapsed ? '10px 0' : '9px 10px',
                    marginBottom: '2px', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', textAlign: 'left',
                    background: isActive ? '#2b6cb0' : 'transparent',
                    color: isActive ? 'white' : '#4a5568',
                    fontSize: '0.88rem', fontWeight: isActive ? '600' : '500',
                    transition: 'all 0.15s', whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f7fafc'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    {item.icon}
                  </svg>
                  {!sidebarCollapsed && item.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* ── Main content ── */}
        <main className="dashboard-content" style={{ flex: 1, minWidth: 0, maxWidth: 'none' }}>
        {/* Gradient Profile Header Card */}
        <div style={{
          background: 'linear-gradient(135deg, #2b6cb0 0%, #3730a3 100%)',
          borderRadius: '12px',
          padding: '28px 32px',
          marginBottom: '24px',
          boxShadow: '0 4px 20px rgba(43,108,176,0.25)'
        }}>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              backgroundColor: 'white', color: '#2b6cb0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.5rem', fontWeight: '700', flexShrink: 0
            }}>
              {patient.first_name?.[0]}{patient.last_name?.[0]}
            </div>

            {/* Patient info */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                <h3 style={{ color: 'white', margin: 0, fontSize: '1.4rem', fontWeight: '700' }}>
                  {patient.first_name} {patient.last_name}
                </h3>
                {patient.relationship_to_admin && (
                  <span style={{
                    padding: '3px 12px', borderRadius: '12px', fontSize: '0.78rem',
                    fontWeight: '600', backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white', textTransform: 'capitalize'
                  }}>
                    {patient.relationship_to_admin}
                  </span>
                )}
              </div>
              <p style={{ color: 'white', margin: '0 0 8px', fontSize: '0.85rem', opacity: 0.9 }}>
                {[
                  age && `${age} yrs`,
                  patient.gender,
                  patient.blood_type && `Blood Type: ${patient.blood_type}`,
                  patient.height && `${patient.height} cm`
                ].filter(Boolean).join(' • ')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '0.85rem', color: 'white', opacity: 0.85 }}>
                {patient.phone && <span>{patient.phone}</span>}
                {patient.email && <span>{patient.email}</span>}
                {patient.date_of_birth && <span>DOB: {new Date(patient.date_of_birth).toLocaleDateString()}</span>}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
              <Link
                to={`/patients/${patientId}/edit`}
                style={{
                  display: 'inline-block', textDecoration: 'none',
                  padding: '8px 16px', borderRadius: '8px', fontWeight: '600',
                  fontSize: '0.85rem', border: '1.5px solid white',
                  color: 'white', background: 'transparent'
                }}
              >
                Edit Profile
              </Link>
              <button
                onClick={handleDownloadPDF}
                style={{ padding: '8px 16px', background: '#e53e3e', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontWeight: '600', fontSize: '0.85rem' }}
              >
                ↓ PDF
              </button>
              <Link
                to={`/patients/${patientId}/vitals/new`}
                style={{
                  display: 'inline-block', textDecoration: 'none',
                  padding: '8px 16px', background: '#38a169', color: 'white',
                  borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem'
                }}
              >
                + Vital
              </Link>
              <Link
                to={`/patients/${patientId}/documents/new`}
                style={{
                  display: 'inline-block', textDecoration: 'none',
                  padding: '8px 16px', background: 'rgba(255,255,255,0.2)', color: 'white',
                  borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem',
                  border: '1.5px solid rgba(255,255,255,0.4)'
                }}
              >
                ↑ Upload
              </Link>
              {isOwnPatient && (
                <button
                  onClick={handleDelete}
                  style={{
                    padding: '8px 16px', background: 'transparent',
                    color: 'white', border: '1.5px solid rgba(255,100,100,0.7)',
                    borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600'
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'lab_results' && <LabResultsTab
            labResults={labResults}
            labSearch={labSearch}
            setLabSearch={setLabSearch}
            labDateRange={labDateRange}
            setLabDateRange={setLabDateRange}
            labCategory={labCategory}
            setLabCategory={setLabCategory}
            labSortDir={labSortDir}
            setLabSortDir={setLabSortDir}
            expandedTrend={expandedTrend}
            toggleTrendChart={toggleTrendChart}
            trendData={trendData}
            normalizeCategory={normalizeCategory}
            cleanTestName={cleanTestName}
            patient={patient}
            setActiveTab={setActiveTab}
            expandedInterpretation={expandedInterpretation}
            loadingInterpretation={loadingInterpretation}
            fetchInterpretation={fetchInterpretation}
            allInterpretations={allInterpretations}
            setAllInterpretations={setAllInterpretations}
            headers={headers}
            allPatientTags={allPatientTags}
            labTagFilter={labTagFilter}
            setLabTagFilter={setLabTagFilter}
            documents={documents}
          />}
          {activeTab === 'vitals' && <VitalsTab />}
          {activeTab === 'health_summary' && (
            <HealthSummaryTab
              patient={patient}
              labResults={labResults}
              vitals={vitals}
              interpretations={allInterpretations}
              headers={headers}
              setAllInterpretations={setAllInterpretations}
            />
          )}
          {activeTab === 'encyclopedia' && (
            <EncyclopediaTab
              headers={headers}
              patientId={patientId}
            />
          )}
          {activeTab === 'medications' && (
            <MedicationsTab
              patient={patient}
              patientId={patientId}
              headers={headers}
            />
          )}
          {activeTab === 'appointments' && <AppointmentsTab />}
          {activeTab === 'documents' && <DocumentsTab />}
        </>
        </main>
      </div>
    </div>
  );
}

export default PatientDashboard;
